import { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
import { AppContext } from "./app.context";
// ModelRwContext removed - Realm EOL Jan 13, 2026
import { ModelTsContext } from "./modelTS.context";
import {
  // MILLISECONDS_PER_DAY,
  JOB_FURNITURE_SKULIST,
  // JOB_SHEETVINYL_SKULIST,
  // JOB_FLOATING_SKULIST,
  // JOB_GLUEDOWN_SKULIST,
  // JOB_NAILDOWN_SKULIST,
  // JOB_TILE_SKULIST,
  // JOB_BACKSPLASH_SKULIST,
  // Surface filter constants
  FILTERS_SURFACE_CARPET,
  FILTERS_SURFACE_VINYL,
  FILTERS_SURFACE_PLANK,
  FILTERS_SURFACE_TILE,
  FILTERS_SURFACE_WOOD,
  FILTERS_SURFACE_BACKSPLASH,
  FILTERS_SURFACE_UNKNOWN,
  FILTERS_SURFACE_DETAIL,
  FILTERS_SURFACE_INTERIORDOOR,
  FILTERS_SURFACE_EXTERIORDOOR,
  FILTERS_SURFACE_STORMDOOR,
  FILTERS_SURFACE_WINDOW,
  // Classification filter constants
  FILTERS_CLASSIFICATION_DETAIL,
  FILTERS_CLASSIFICATION_JOB,
  FILTERS_CLASSIFICATION_RELATED_PO,
  FILTERS_CLASSIFICATION_WORKORDER_EXPENSE,
  FILTERS_CLASSIFICATION_REPAIR_ORDER,
  FILTERS_CLASSIFICATION_PAID_IN_ERROR,
  FILTERS_CLASSIFICATION_OTHER,
} from "../utility/keys";
import { buildDragNDropUpdateFields } from "../utility/dragNDropUpdateFields";
import { gateParkingForTimeTravel } from "../utility/gateParkingForTimeTravel";
import { isRepairOrderWithInstaller } from "../utility/isRepairOrderWithInstaller";
import { categorizeRepairOrderDrop } from "../utility/repairOrderDropType";
import { isUnassignedOpenWork } from "../utility/isUnassignedOpenWork";

// - - - - - - - - - - - - - - - - - - - -

const emptyArray = [];

// - - - - - - - - - - - - - - - - - - - -

export const ScheduleContext = createContext();

// - - - - - - - - - -

/**
 * @param {boolean} useRightSide  Time Machine #114 Phase 6 Diff Pair view —
 *   when true, this provider sources its time-travel data from the T2
 *   replay slots (`timeTravelProjectsResultRight` etc.) instead of the
 *   T1 slots. Used by the second ScheduleContextProvider instance mounted
 *   above the right grid in the Optimizer's split-screen Diff Pair view.
 *   Live mode is unaffected — the prop only branches inside the
 *   `isTimeTraveling` arms of the data-source aliases. Mutations are
 *   already triple-blocked while time-traveling (ReadOnlyShield,
 *   processDragNDrop early-return, mutation-hook throws), so two
 *   ScheduleContexts in the tree is safe.
 */
export const ScheduleContextProvider = ({ children, useRightSide = false }) => {
  const [scheduleArray, setScheduleArray] = useState(emptyArray);
  const [countsArray, setCountsArray] = useState(emptyArray);
  const [furnitureArray, setFurnitureArray] = useState(emptyArray);
  const [floatingArray, setFloatingArray] = useState(emptyArray);
  const [glueDownArray, setGlueDownArray] = useState(emptyArray);
  const [nailDownArray, setNailDownArray] = useState(emptyArray);
  const [vinylArray, setVinylArray] = useState(emptyArray);
  const [tileArray, setTileArray] = useState(emptyArray);
  const [backsplashArray, setBacksplashArray] = useState(emptyArray);
  const [interiorDoorArray, setInteriorDoorArray] = useState(emptyArray);
  const [exteriorDoorArray, setExteriorDoorArray] = useState(emptyArray);
  const [stormDoorArray, setStormDoorArray] = useState(emptyArray);
  const [windowArray, setWindowArray] = useState(emptyArray);
  const [schedulePreparing, setSchedulePreparing] = useState(false);
  const [scheduleNeedsModel, setScheduleNeedsModel] = useState(false);
  const [processingDragNDrop, setProcessingDragNDrop] = useState(false);
  const [dragObject, setDragObject] = useState("");
  const [dragObjectInvoiceNo, setDragObjectInvoiceNo] = useState(null); // Stored at drag start for tanstackOnly fallback
  const [dropObject, setDropObject] = useState({ id: "" });
  // pendingDragUpdate holds the update values set by JobInitModal for tanstackOnly users
  // This is needed because TanStack Query data is immutable - modifying the found document
  // in JobInitModal doesn't persist to processDragNDrop's lookup
  const [pendingDragUpdate, setPendingDragUpdate] = useState(null);
  const [projectListQuery, setProjectListQuery] = useState("");
  const [userListQuery, setUserListQuery] = useState("");
  const [selectedUnscheduledJob, setSelectedUnscheduledJob] = useState(null);

  // Job Parking state - allows users to "park" jobs for moving between weeks
  // Structure: [{ id, invoiceNo, originalInstaller, originalDateScheduled, parkedAt }]
  const [parkedJobs, setParkedJobs] = useState([]);

  // Optimizer "Show 'OFF'" checkbox (issue #100) - per-user, localStorage-persisted.
  // UX semantic: checked = show all rows (including all-OFF ones);
  //              unchecked = hide home installers whose entire week is OFF.
  // Default is true (checked) — matches the pre-#100 behavior where all rows
  // were visible. Users opt-in to hiding by unchecking the box.
  const [showOffInstallers, setShowOffInstallers] = useState(true);

  // Signal to auto-expand the Suppressed accordion after a suppress action
  const [autoExpandSuppressed, setAutoExpandSuppressed] = useState(false);

  const {
    // appEnvirons,
    companyCode,
    locationFilter,
    locationArray,
    weekDatesArray,
    createUuid,
    dataFormatting_dateFields,
    dataFormatting_payrollFields,
    dataFormatting_encodedSmallTitleNew,
    dataFormatting_encodedLargeTitleNew,
    jobTitleConfig,
    setOpenJobInitModal,
    setOpenSuppressModal,
    setOpenRepairOrderConfirmModal,
    // setLocationArray, // REMOVED: Don't clear locationArray on location change - it's the dropdown options
    screenAccess,
    currentFilters,
    // openCrewDetailModal removed - THE BIG MOVE useEffect removed (Realm EOL)
    // Time Machine #114 Phase 4 — read-only time-travel mode. When true, this
    // context's data sources swap from live TanStack results to the
    // historical-state results from ModelTsContext, and processDragNDrop
    // early-returns (defense-in-depth on top of UI-level disabling).
    isTimeTraveling,
  } = useContext(AppContext);
  // ModelRwContext removed - Realm EOL Jan 13, 2026
  // All data now comes from ModelTsContext (TanStack Query)
  const {
    // setAwsgwUniqueLocationsResult, // REMOVED: Don't clear unique locations on location change
    setAwsgwOfficeMetaDataResult,
    setAwsgwUserResult,
    setAwsgwUsersResult,
    setAwsgwProjectsResult,
    setAwsgwCandidatesResult,
    setAwsgwCandidatesAdditionalResult,
    setAwsgwAttributesResult,
    setAwsgwLogsResult,createAdditionalCandidateQueryList,
    awsgwGetUniqueLocations,
    awsgwGetOfficeMetaDataByLocation,
    awsgwGetUsers,
    awsgwGetProjectsByCompany,
    awsgwGetCandidatesByLocation,
    awsgwGetAttributesByLocation,
    awsgwGetLogs,
    setExpectedQueries,
    setCompletedQueries,
    setConsolidatedLogQueries,
    resetQueryCompletionTracking,
    setRealmRequestedProjectsList,
    // setShouldFetchUniqueLocations, // REMOVED: Don't reset unique locations trigger on location change
    setShouldFetchOfficeMetaData,
    setShouldFetchUser,
    setShouldFetchUsers,
    setShouldFetchProject,
    setShouldFetchProjects,
    setShouldFetchCandidates,
    setShouldFetchCandidatesAdditional,
    setShouldFetchProjectsAdditional,
    setShouldFetchAttributes,
    setShouldFetchLogs,
    setShouldPollProjects,
    setShouldPollAttributes,
    mongoUser: tsMongoUser, // NOW ACTIVE: Primary auth state
    tsUserDocument, // NOW ACTIVE: Primary user document
    projectListAfterMerge, // TanStack project data (replaces projectsList)
    // projectListAfterMergeLocation removed - THE BIG MOVE useEffect removed (Realm EOL)
    awsgwCandidatesResult, // TanStack candidates data (replaces candidatesList)
    awsgwAttributesResult, // TanStack attributes data (replaces attributesList)
    // awsgwAttributesResultLocation removed - THE BIG MOVE useEffect removed (Realm EOL)
    // Time Machine #114 Phase 4 — historical-state slots. Empty arrays/object
    // when not time-traveling (effect in ModelTsContext clears them on exit).
    timeTravelProjectsResult,
    timeTravelCandidatesResult,
    timeTravelAttributesResult,
    // Time Machine #114 Phase 6 — Diff Pair right-state slots. Populated
    // when isDiffPairView is true; empty arrays otherwise. Consumed by this
    // provider only when `useRightSide` prop is true.
    timeTravelProjectsResultRight,
    timeTravelCandidatesResultRight,
    timeTravelAttributesResultRight,
    // Loading states
    projectsLoading,
    candidatesLoading,
    attributesLoading,
    locationsLoading: uniqueLocationsLoading,
    // TanStack mutations
    setScheduleById: tsSetScheduleById,
    // tsSetSuppressionById not used in this context
    // tsSetAttributeById not used in this context
    // tsCreateNewAttribute not used in this context
  } = useContext(ModelTsContext);
  // Use TanStack context as primary auth source
  const mongoUser = tsMongoUser;
  const userDocument = tsUserDocument;
  // Alias TanStack data to match legacy variable names used throughout this file.
  //
  // Time Machine #114 Phase 4: when isTimeTraveling, swap to the historical
  // result slot populated by ModelTsContext's getStateAtTimestamp query. The
  // schedule grid + filteredProjectsList useMemo + downstream consumers are
  // oblivious to which path they're reading from.
  // Phase 6: when useRightSide is true (right grid of Diff Pair view), we are
  // always in time-travel mode (the right provider only mounts under
  // isDiffPairView, which gates on isTimeTraveling). Pull T2 instead of T1.
  // Wrapped in useMemo so the `|| []` fallback returns the SAME empty array
  // across renders — without it, filteredProjectsList's deps change on every
  // render (react-hooks/exhaustive-deps warning).
  const projectsListSource = useMemo(() => {
    if (isTimeTraveling) {
      if (useRightSide) return timeTravelProjectsResultRight || [];
      return timeTravelProjectsResult || [];
    }
    return projectListAfterMerge;
  }, [
    isTimeTraveling,
    useRightSide,
    timeTravelProjectsResultRight,
    timeTravelProjectsResult,
    projectListAfterMerge,
  ]);
  const projectsList = projectsListSource;
  // Filter projects to remove cancelled/old jobs (was in ModelRwContext.updateFilteredProjectsList)
  // 1. Remove cancellations (statusCode = 20)
  // 2. Remove jobs not ready: only show if (statusCode >= 3 && unscheduled) OR scheduled
  // 3. For UNSCHEDULED jobs only: filter by current location (RTS should only show current location)
  //    Scheduled jobs can be from any location (shared installer feature - 2025-02-14)
  // 4. Apply user's surface filters (from User document)
  // 5. Apply user's classification filters (from User document)
  // useMemo prevents infinite loop - filter creates new array each render
  // NOTE: statusCode can be string or number, so use parseFloat for comparisons
  const filteredProjectsList = useMemo(() => {
    let filtered = (projectsListSource || []).filter(
      (doc) => {
        const statusCode = parseFloat(doc.statusCode);
        // #152 (2026-05-21, Brandon) — unscheduled Repair Orders at status 30
        // (Repair Open) must surface in the Scheduler so they appear in the
        // Created/Verified panel AND are eligible for Tier 3 recommendations.
        // Previously the unscheduled-clause only allowed statusCode 1-4,
        // silently dropping 96 status-30 Repair Order docs in UAT. Brandon's
        // examples: 2520878, 2517558, 2505331, 2491387. The new clause is
        // gated by classification === "Repair Order" via `isUnassignedOpenWork`
        // — also covers the existing #82 Created/Verified sub-statuses (1.x, 2.x).
        const isUnscheduledStatusOneToFour =
          statusCode >= 1 && statusCode <= 4 && doc.date_scheduled === "";
        const isUnscheduledAndOpen =
          doc.date_scheduled === "" && isUnassignedOpenWork(doc);
        return (
          statusCode !== 20 &&
          (isUnscheduledStatusOneToFour ||
            isUnscheduledAndOpen ||
            doc.date_scheduled !== "") &&
          // Location filter: unscheduled jobs must match current location, scheduled jobs can be from any location
          (doc.date_scheduled !== "" || doc.location === locationFilter)
        );
      }
    );

    // Apply surface filters (if filter is OFF/false, exclude that surface type)
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_DETAIL]) {
      filtered = filtered.filter((doc) => doc.surface !== "Detail");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_CARPET]) {
      filtered = filtered.filter((doc) => doc.surface !== "Carpet");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_VINYL]) {
      filtered = filtered.filter((doc) => doc.surface !== "Vinyl");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_PLANK]) {
      filtered = filtered.filter((doc) => doc.surface !== "Plank");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_TILE]) {
      filtered = filtered.filter((doc) => doc.surface !== "Tile");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_WOOD]) {
      filtered = filtered.filter((doc) => doc.surface !== "Wood");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_BACKSPLASH]) {
      filtered = filtered.filter((doc) => doc.surface !== "Backsplash");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_INTERIORDOOR]) {
      filtered = filtered.filter((doc) => doc.surface !== "InteriorDoor");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_EXTERIORDOOR]) {
      filtered = filtered.filter((doc) => doc.surface !== "ExteriorDoor");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_STORMDOOR]) {
      filtered = filtered.filter((doc) => doc.surface !== "StormDoor");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_WINDOW]) {
      filtered = filtered.filter((doc) => doc.surface !== "Window");
    }
    if (!currentFilters.surfaceFilters[FILTERS_SURFACE_UNKNOWN]) {
      filtered = filtered.filter((doc) => doc.surface !== "UNK");
    }

    // Apply classification filters (if filter is OFF/false, exclude that classification)
    if (!currentFilters.classificationFilters[FILTERS_CLASSIFICATION_DETAIL]) {
      filtered = filtered.filter((doc) => doc.classification !== "Detail");
    }
    if (!currentFilters.classificationFilters[FILTERS_CLASSIFICATION_JOB]) {
      filtered = filtered.filter((doc) => doc.classification !== "Job");
    }
    if (!currentFilters.classificationFilters[FILTERS_CLASSIFICATION_RELATED_PO]) {
      filtered = filtered.filter((doc) => doc.classification !== "Related PO");
    }
    if (!currentFilters.classificationFilters[FILTERS_CLASSIFICATION_WORKORDER_EXPENSE]) {
      filtered = filtered.filter((doc) => doc.classification !== "Workorder/Expense");
    }
    if (!currentFilters.classificationFilters[FILTERS_CLASSIFICATION_REPAIR_ORDER]) {
      filtered = filtered.filter((doc) => doc.classification !== "Repair Order");
    }
    if (!currentFilters.classificationFilters[FILTERS_CLASSIFICATION_PAID_IN_ERROR]) {
      filtered = filtered.filter((doc) => doc.classification !== "Paid In Error");
    }
    if (!currentFilters.classificationFilters[FILTERS_CLASSIFICATION_OTHER]) {
      filtered = filtered.filter((doc) => doc.classification !== "Other");
    }

    return filtered;
  }, [projectsListSource, locationFilter, currentFilters]);
  const candidatesList = isTimeTraveling
    ? useRightSide
      ? (timeTravelCandidatesResultRight || [])
      : (timeTravelCandidatesResult || [])
    : awsgwCandidatesResult;
  const attributesList = isTimeTraveling
    ? useRightSide
      ? (timeTravelAttributesResultRight || [])
      : (timeTravelAttributesResult || [])
    : awsgwAttributesResult;
  const locationsLoading = uniqueLocationsLoading;

  // Refs to hold latest project data to avoid stale closure in processDragNDrop
  const projectListAfterMergeRef = useRef(projectListAfterMerge);
  useEffect(() => {
    projectListAfterMergeRef.current = projectListAfterMerge;
  }, [projectListAfterMerge]);

  const projectsListRef = useRef(projectsList);
  useEffect(() => {
    projectsListRef.current = projectsList;
  }, [projectsList]);

  // Ref to hold latest filteredProjectsList - this is what the UI actually renders from
  const filteredProjectsListRef = useRef(filteredProjectsList);
  useEffect(() => {
    filteredProjectsListRef.current = filteredProjectsList;
  }, [filteredProjectsList]);

  // Ref to hold latest scheduleArray - critical for tanstackOnly drag-n-drop lookup
  const scheduleArrayRef = useRef(scheduleArray);
  useEffect(() => {
    scheduleArrayRef.current = scheduleArray;
  }, [scheduleArray]);

  // ========== JOB PARKING - localStorage sync and helper functions ==========
  // localStorage key uses user ID for user-specific parking
  const parkedJobsStorageKey = `scheduler_parkedJobs_${tsUserDocument?._id || tsUserDocument?.username || "unknown"}`;

  // Load parked jobs from localStorage on mount (when user is available)
  useEffect(() => {
    if (tsUserDocument?._id || tsUserDocument?.username) {
      try {
        const stored = localStorage.getItem(parkedJobsStorageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            setParkedJobs(parsed);
            console.log("Job Parking: Loaded from localStorage", parsed.length, "jobs");
          }
        }
      } catch (err) {
        console.error("Job Parking: Failed to load from localStorage", err);
      }
    }
  }, [parkedJobsStorageKey, tsUserDocument?._id, tsUserDocument?.username]);

  // Save parked jobs to localStorage whenever they change
  useEffect(() => {
    if (tsUserDocument?._id || tsUserDocument?.username) {
      try {
        localStorage.setItem(parkedJobsStorageKey, JSON.stringify(parkedJobs));
        console.log("Job Parking: Saved to localStorage", parkedJobs.length, "jobs");
      } catch (err) {
        console.error("Job Parking: Failed to save to localStorage", err);
      }
    }
  }, [parkedJobs, parkedJobsStorageKey, tsUserDocument?._id, tsUserDocument?.username]);

  // ========== OPTIMIZER "Show 'OFF'" CHECKBOX — localStorage sync (issue #100) ==========
  // Mirrors the parking pattern above: per-user key, read on mount, write on change.
  const showOffInstallersStorageKey = `scheduler_showOffInstallers_${tsUserDocument?._id || tsUserDocument?.username || "unknown"}`;

  useEffect(() => {
    if (tsUserDocument?._id || tsUserDocument?.username) {
      try {
        const stored = localStorage.getItem(showOffInstallersStorageKey);
        if (stored === "true") setShowOffInstallers(true);
        else if (stored === "false") setShowOffInstallers(false);
      } catch (err) {
        console.error("Show OFF checkbox: Failed to load from localStorage", err);
      }
    }
  }, [showOffInstallersStorageKey, tsUserDocument?._id, tsUserDocument?.username]);

  useEffect(() => {
    if (tsUserDocument?._id || tsUserDocument?.username) {
      try {
        localStorage.setItem(showOffInstallersStorageKey, String(showOffInstallers));
      } catch (err) {
        console.error("Show OFF checkbox: Failed to save to localStorage", err);
      }
    }
  }, [showOffInstallers, showOffInstallersStorageKey, tsUserDocument?._id, tsUserDocument?.username]);

  // Auto-cleanup stale parked jobs when project data updates (e.g. job cancelled/completed by another user)
  // Only validates parked jobs for the CURRENT location — parked jobs from other locations are left untouched (#77)
  // Validates against unfiltered projectListAfterMerge so surface/classification filters don't evict valid parked jobs
  //
  // Time Machine #114 #120 — Skip during time-travel. projectListAfterMerge
  // is the HISTORICAL data during time-travel; running the cleanup against it
  // would evict any live-parked job whose ID isn't in the historical snapshot
  // (e.g., a job created AFTER the time-travel target moment but parked in
  // live mode). The eviction is permanent (writes to localStorage), so the
  // user would silently lose the parked job after exiting time-travel.
  // Cleanup resumes naturally once isTimeTraveling flips back to false and
  // the live projectListAfterMerge populates.
  useEffect(() => {
    if (isTimeTraveling) return;
    if (parkedJobs.length === 0 || !projectListAfterMerge?.length) return;
    const validIds = new Set(projectListAfterMerge.map((j) => j.id));
    // Split parked jobs: current location vs other locations
    const currentLocationParked = parkedJobs.filter((pj) => !pj.location || pj.location === locationFilter);
    const otherLocationParked = parkedJobs.filter((pj) => pj.location && pj.location !== locationFilter);
    // Only validate current location's parked jobs against the project list
    const validCurrentParked = currentLocationParked.filter((pj) => validIds.has(pj.id));
    const evictedCount = currentLocationParked.length - validCurrentParked.length;
    if (evictedCount > 0) {
      console.log(
        `%cJob Parking: Auto-cleaned ${evictedCount} stale parked job(s) from ${locationFilter}`,
        "color: #f97316; font-weight: bold;"
      );
      setParkedJobs([...validCurrentParked, ...otherLocationParked]);
    }
  }, [projectListAfterMerge]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Park a job - adds to parkedJobs state (persisted via localStorage)
   * @param {string} jobId - The job's ID (mongoId)
   * @param {number|string} invoiceNo - Invoice number for reference
   * @param {string} installer - Original installer ID
   * @param {string} dateScheduled - Original scheduled date
   */
  const parkJob = (jobId, invoiceNo, installer, dateScheduled) => {
    setParkedJobs((prev) => {
      // Don't add duplicates
      if (prev.some((pj) => pj.id === jobId)) {
        console.log("Job Parking: Job already parked", jobId);
        return prev;
      }
      const newParked = {
        id: jobId,
        invoiceNo,
        originalInstaller: installer,
        originalDateScheduled: dateScheduled,
        location: locationFilter, // track which location this job was parked from (#77)
        parkedAt: new Date().toISOString(),
      };
      console.log("Job Parking: Parked job", newParked);
      return [...prev, newParked];
    });
  };

  /**
   * Unpark a job - removes from parkedJobs state
   * @param {string} jobId - The job's ID to unpark
   */
  const unparkJob = (jobId) => {
    setParkedJobs((prev) => {
      const filtered = prev.filter((pj) => pj.id !== jobId);
      if (filtered.length !== prev.length) {
        console.log("Job Parking: Unparked job", jobId);
      }
      return filtered;
    });
  };

  /**
   * Check if a job is currently parked
   * @param {string} jobId - The job's ID to check
   * @returns {boolean} - True if job is parked
   */
  const isJobParked = (jobId) => {
    return parkedJobs.some((pj) => pj.id === jobId);
  };
  // ========== END JOB PARKING ==========

  // Note: dragObjectInvoiceNo is now set directly by UI components via setDragObjectInvoiceNo
  // This avoids the stale scheduleArray lookup issue - the UI component passes the invoiceNo
  // from the actual project object being dragged, which is guaranteed to be correct.

  // - - - - - - - - - -

  /**
   * Processes drag and drop actions for scheduling.
   * Only JOBS (projects) can be dragged - attributes are not draggable.
   */
  const processDragNDrop = () => {
    // Time Machine #114 Phase 4 — defense-in-depth guard. The UI disables the
    // schedule grid's drag handles while time-traveling, but a guard here
    // makes the invariant impossible to violate even if a drop event somehow
    // dispatches. This mirrors the mutation-layer guards in ModelTsContext
    // (#114 three-layer-defense pattern).
    if (isTimeTraveling) {
      console.warn(
        "%cprocessDragNDrop: blocked — time-travel mode is active (#114)",
        "color: #DAA520; font-weight: bold;"
      );
      return;
    }
    const conversion = userDocument?.conversion || "both";
    const itemId = dragObject;

    // ========== DEBUG: Comprehensive logging for drag-and-drop troubleshooting ==========
    console.log(
      "%c========== DRAG-N-DROP DEBUG START ==========",
      "color: #ff6600; font-weight: bold; font-size: 14px;"
    );
    console.log("dragObject (itemId):", itemId);
    console.log("dropObject:", dropObject);
    console.log("conversion:", conversion);

    // Log all source list states
    console.log("%c--- SOURCE LIST STATES ---", "color: #0099ff; font-weight: bold;");
    console.log("projectListAfterMergeRef.current length:", projectListAfterMergeRef.current?.length || 0);
    console.log("projectListAfterMerge length:", projectListAfterMerge?.length || 0);
    console.log("filteredProjectsListRef.current length:", filteredProjectsListRef.current?.length || 0);
    console.log("filteredProjectsList length:", filteredProjectsList?.length || 0);
    console.log("projectsListRef.current length:", projectsListRef.current?.length || 0);
    console.log("projectsList length:", projectsList?.length || 0);

    // Define source lists based on conversion type
    const sources = conversion === "tanstackOnly"
      ? [
          { data: projectListAfterMergeRef.current, name: "projectListAfterMerge (ref)" },
          { data: projectListAfterMerge, name: "projectListAfterMerge (direct)" },
          { data: filteredProjectsListRef.current, name: "filteredProjectsList (ref)" },
          { data: filteredProjectsList, name: "filteredProjectsList (direct)" },
          { data: projectsListRef.current, name: "projectsList (ref)" },
          { data: projectsList, name: "projectsList (direct)" },
        ]
      : [
          { data: projectsListRef.current, name: "projectsList (ref)" },
          { data: projectsList, name: "projectsList (direct)" },
          { data: filteredProjectsListRef.current, name: "filteredProjectsList (ref)" },
          { data: filteredProjectsList, name: "filteredProjectsList (direct)" },
        ];

    let foundDocument = null;
    let dataSourceName = "";
    let searchMethod = "";

    // For tanstackOnly users: itemId IS the mongoId (since TanStack transform sets id=mongoId)
    // For both/realmOnly users: itemId is a custom UUID, need to look up in scheduleArray first
    if (conversion === "tanstackOnly") {
      // TANSTACK PATH: itemId is the mongoId, search source lists directly
      console.log("%c--- TANSTACK: SEARCHING BY ID (mongoId) ---", "color: #0099ff; font-weight: bold;");
      console.log("itemId to find:", itemId, "type:", typeof itemId);

      // Use scheduleArrayRef to get the latest data (avoids stale closure)
      const currentScheduleArray = scheduleArrayRef.current || scheduleArray;
      console.log("scheduleArrayRef.current length:", scheduleArrayRef.current?.length, ", scheduleArray length:", scheduleArray?.length);

      // First, try to find in scheduleArray to get invoiceNo for fallback search
      const scheduleItem = currentScheduleArray.find((item) => String(item.id || "") === itemId);
      const fallbackInvoiceNo = scheduleItem?.invoiceNo;
      console.log("scheduleArray lookup - found:", !!scheduleItem, ", invoiceNo for fallback:", fallbackInvoiceNo);

      // DEBUG: If not found, log some sample IDs from scheduleArray to help diagnose
      if (!scheduleItem && currentScheduleArray.length > 0) {
        console.log("%c--- DEBUG: scheduleArray sample IDs ---", "color: #ff6600;");
        const samples = currentScheduleArray.slice(0, 5).map(item => ({
          id: item.id,
          invoiceNo: item.invoiceNo,
          start: item.start
        }));
        console.log("First 5 items:", samples);
        // Check if the itemId exists in ANY item's id field
        const anyMatch = currentScheduleArray.some(item =>
          String(item.id || "").includes(itemId.slice(0, 10)) ||
          String(itemId).includes(String(item.id || "").slice(0, 10))
        );
        console.log("Any partial match:", anyMatch);
      }

      for (const source of sources) {
        const hasData = source.data && source.data.length > 0;
        console.log(`Checking ${source.name}: hasData=${hasData}, length=${source.data?.length || 0}`);
        if (hasData) {
          // Debug: show first item's id fields to understand data structure
          if (source.data.length > 0) {
            const sample = source.data[0];
            console.log(`  Sample item - id: ${sample.id} (${typeof sample.id}), mongoId: ${sample.mongoId}, _id: ${sample._id} (${typeof sample._id})`);
          }
          // For TanStack: id field = mongoId, so search by id directly
          // Convert all to strings for comparison since _id might be ObjectId
          const found = source.data.find((doc) => {
            const docId = String(doc.id || "");
            const docMongoId = String(doc.mongoId || "");
            const doc_id = String(doc._id || "");
            return docId === itemId || docMongoId === itemId || doc_id === itemId;
          });
          console.log(`  -> Found by mongoId: ${!!found}`);
          if (found) {
            foundDocument = found;
            dataSourceName = source.name;
            searchMethod = "direct-mongoId";
            console.log(`  -> MATCH! Using this source.`);
            break;
          }
        }
      }

      // FALLBACK: If not found by mongoId, try by invoiceNo (handles data refresh scenarios)
      // Use fallbackInvoiceNo from current scheduleArray lookup, OR dragObjectInvoiceNo passed from UI at drag start
      const invoiceNoToSearch = fallbackInvoiceNo || dragObjectInvoiceNo;
      if (!foundDocument && invoiceNoToSearch) {
        console.log("%c--- TANSTACK FALLBACK: SEARCHING BY INVOICENO ---", "color: #ff9900; font-weight: bold;");
        console.log("Using invoiceNo:", invoiceNoToSearch, "type:", typeof invoiceNoToSearch, "(from scheduleArray:", !!fallbackInvoiceNo, ", from dragObjectInvoiceNo:", !!dragObjectInvoiceNo, ")");
        // Convert to both string and number for comparison (invoiceNo might be stored as either type)
        const invoiceNoStr = String(invoiceNoToSearch);
        const invoiceNoNum = Number(invoiceNoToSearch);
        for (const source of sources) {
          const hasData = source.data && source.data.length > 0;
          if (hasData) {
            // Debug: show first item's invoiceNo type
            if (source.data.length > 0) {
              const sample = source.data[0];
              console.log(`  ${source.name} sample invoiceNo: ${sample.invoiceNo} (type: ${typeof sample.invoiceNo})`);
            }
            // Compare using both string and number to handle type mismatches
            const found = source.data.find((doc) =>
              String(doc.invoiceNo) === invoiceNoStr || doc.invoiceNo === invoiceNoNum
            );
            console.log(`  Checking ${source.name} by invoiceNo ${invoiceNoToSearch}: ${!!found}`);
            if (found) {
              foundDocument = found;
              dataSourceName = source.name;
              searchMethod = "invoiceNo-fallback";
              console.log(`  -> MATCH via invoiceNo! Using this source.`);
              break;
            }
          }
        }
      }
    } else {
      // REALM PATH: itemId is a custom UUID, need to find in scheduleArray first to get mongoId
      console.log("%c--- REALM: LOOKING UP IN SCHEDULE ARRAY ---", "color: #0099ff; font-weight: bold;");
      const scheduleItem = scheduleArray.find((item) => item.id === itemId);
      console.log("scheduleArray length:", scheduleArray?.length || 0);
      console.log("Found in scheduleArray:", !!scheduleItem);

      if (!scheduleItem) {
        console.log(
          "%c========== DRAG-N-DROP FAILED - NOT IN SCHEDULE ARRAY ==========",
          "color: #ff0000; font-weight: bold; font-size: 14px;"
        );
        setDragObject("");
        setDropObject({ id: "" });
        setProcessingDragNDrop(false);
        return;
      }

      console.log("scheduleItem - id:", scheduleItem.id, ", mongoId:", scheduleItem.mongoId, ", invoiceNo:", scheduleItem.invoiceNo);

      // Determine the best key to search by
      const searchMongoId = scheduleItem.mongoId || scheduleItem._id?.toString?.() || scheduleItem._id;
      const searchInvoiceNo = scheduleItem.invoiceNo;
      console.log("Search keys - mongoId:", searchMongoId, ", invoiceNo:", searchInvoiceNo);

      // SEARCH ATTEMPT 1: Try by mongoId
      if (searchMongoId) {
        console.log("%c--- SEARCHING BY MONGOID ---", "color: #0099ff; font-weight: bold;");
        for (const source of sources) {
          const hasData = source.data && source.data.length > 0;
          console.log(`Checking ${source.name}: hasData=${hasData}, length=${source.data?.length || 0}`);
          if (hasData) {
            const found = source.data.find((doc) =>
              doc.id === searchMongoId || doc.mongoId === searchMongoId
            );
            console.log(`  -> Found by mongoId: ${!!found}`);
            if (found) {
              foundDocument = found;
              dataSourceName = source.name;
              searchMethod = "mongoId";
              console.log(`  -> MATCH! Using this source.`);
              break;
            }
          }
        }
      }

      // SEARCH ATTEMPT 2: Fall back to invoiceNo if mongoId search failed
      if (!foundDocument && searchInvoiceNo) {
        console.log("%c--- FALLBACK: SEARCHING BY INVOICENO ---", "color: #ff9900; font-weight: bold;");
        for (const source of sources) {
          const hasData = source.data && source.data.length > 0;
          console.log(`Checking ${source.name}: hasData=${hasData}, length=${source.data?.length || 0}`);
          if (hasData) {
            const found = source.data.find((doc) => doc.invoiceNo === searchInvoiceNo);
            console.log(`  -> Found by invoiceNo: ${!!found}`);
            if (found) {
              foundDocument = found;
              dataSourceName = source.name;
              searchMethod = "invoiceNo";
              console.log(`  -> MATCH! Using this source.`);
              break;
            }
          }
        }
      }
    }

    if (!foundDocument) {
      console.log(
        "%c========== DRAG-N-DROP FAILED - NOT FOUND IN SOURCE LISTS ==========",
        "color: #ff0000; font-weight: bold; font-size: 14px;"
      );
      console.log("itemId:", itemId);
      setDragObject("");
      setDropObject({ id: "" });
      setProcessingDragNDrop(false);
      return;
    }

    console.log(
      "%c========== DRAG-N-DROP SUCCESS ==========",
      "color: #00ff00; font-weight: bold; font-size: 14px;"
    );
    console.log("Found in:", dataSourceName, "via", searchMethod);
    console.log("foundDocument - id:", foundDocument.id, ", _id:", foundDocument._id, ", mongoId:", foundDocument.mongoId, ", invoiceNo:", foundDocument.invoiceNo);
    console.log("foundDocument._id type:", typeof foundDocument._id, ", value:", foundDocument._id);
    console.log("foundDocument._id JSON:", JSON.stringify(foundDocument._id));
    console.log("foundDocument full object keys:", Object.keys(foundDocument));
    // Check if _id is an extended JSON object like { $oid: "..." }
    if (foundDocument._id && typeof foundDocument._id === 'object' && foundDocument._id.$oid) {
      console.log("foundDocument._id.$oid (extended JSON):", foundDocument._id.$oid);
    }

    // update MongoDB with scheduledDate
    //
    //     THIS CHANGES MONGODB DATA, WHICH THEN TRIGGERS FUNCTION TO UPDATE FILEMAKER VIA HOOKDECK
    //
    // Use TanStack mutation (Realm EOL Jan 13, 2026)
    // IMPORTANT: For TanStack data, _id might equal mongoId (a string). We need to use the actual MongoDB _id.
    const mutationId = foundDocument._id?.toString() || foundDocument.mongoId || foundDocument.id;
    console.log(
      "ScheduleContext processDragNDrop - calling tsSetScheduleById.mutate"
    );
    console.log("MUTATION ID being sent:", mutationId, "type:", typeof mutationId);

    // Use pendingDragUpdate values set by JobInitModal
    // TanStack data is immutable, so modifications made in JobInitModal don't persist to this lookup
    const updateValues = pendingDragUpdate || {};
    console.log("pendingDragUpdate:", pendingDragUpdate);

    // Build updateFields via the pure buildDragNDropUpdateFields function.
    // Extracted to src/utility/dragNDropUpdateFields.js (#85) so tests import
    // the EXACT production logic — no test-helper drift (#73, #80, #83, #84).
    // Dates use semantic comparison via datesEqual() to avoid zero-pad
    // mismatches ("04/26/2026" vs "4/26/2026"); see file header for rationale.
    const history = new Date()
      .toString()
      .concat(" - ")
      .concat(tsUserDocument?.username || "unknown")
      .concat(", tab:schedule");
    const scheduleUpdateFields = buildDragNDropUpdateFields(
      foundDocument,
      updateValues,
      history,
      tsUserDocument?.username || "unknown"
    );

    console.log("UPDATE FIELDS:", scheduleUpdateFields, "invoiceNo:", foundDocument.invoiceNo);

    tsSetScheduleById.mutate({
      id: mutationId,
      updateFields: scheduleUpdateFields,
    });
    // Clear pendingDragUpdate after use
    setPendingDragUpdate(null);
    createCountsArray();
    createFurnitureArray();
    createFloatingArray();
    createGlueDownArray();
    createNailDownArray();
    createVinylArray();
    createTileArray();
    createBacksplashArray();
    createInteriorDoorArray();
    createExteriorDoorArray();
    createStormDoorArray();
    createWindowArray();
    // Note: updateFilteredProjectsList needs a data source array, but we found by id not from a specific array
    // The mutation will trigger polling which will refresh the data

    // If this job was parked, unpark it now that it's been moved
    unparkJob(itemId);

    setDragObject("");
    setDropObject({ id: "" });
    setProcessingDragNDrop(false);
  };

  // - - - - - - - - - -

  /**
   * Triggers the loading of model data when the schedule needs it.
   */
  const onScheduleNeedsModel = () => {
    // Clear previous data immediately so old location data doesn't persist during queries
    // NOTE: Do NOT clear locationArray here - it contains the dropdown options which are
    // independent of the current location filter. Clearing it causes the dropdown to go blank.
    // Clear schedule array so UI shows empty/loading state immediately
    setScheduleArray([]);

    // Clear TanStack results (Realm EOL Jan 13, 2026 - all users use TanStack)
    // NOTE: Do NOT clear awsgwUniqueLocationsResult - it contains the dropdown options
    // which are independent of the current location filter.
    setAwsgwOfficeMetaDataResult({});
    setAwsgwUserResult({});
    setAwsgwUsersResult([]);
    setAwsgwProjectsResult([]);
    setAwsgwCandidatesResult([]);
    setAwsgwCandidatesAdditionalResult([]);
    setAwsgwAttributesResult([]);
    setAwsgwLogsResult([]);

    // Clear consolidated log state
    setExpectedQueries(new Set());
    setCompletedQueries(new Set());
    setConsolidatedLogQueries([]);
    resetQueryCompletionTracking(); // Reset hasCompleted refs so queries can be tracked again

    setRealmRequestedProjectsList([]);

    // Reset TanStack query triggers to allow re-fetch on location change
    // NOTE: Do NOT reset setShouldFetchUniqueLocations - unique locations don't change per location
    setShouldFetchOfficeMetaData(false);
    setShouldFetchUser(false);
    setShouldFetchUsers(false);
    setShouldFetchProject(false);
    setShouldFetchProjects(false);
    setShouldFetchCandidates(false);
    setShouldFetchCandidatesAdditional(false);
    setShouldFetchProjectsAdditional(false);
    setShouldFetchAttributes(false);
    setShouldFetchLogs(false);

    // Disable polling during location change - will re-enable after new load completes
    setShouldPollProjects(false);
    setShouldPollAttributes(false);

    // schedule needs updated Model data
    // REALM DISABLED - 2026-01-13 (Soft removal phase)
    // All users are tanstackOnly now - no need to wait for rwMongoUser
    const canProceedWithQueries = !!mongoUser;

    if (canProceedWithQueries) {
      // TanStack-only users proceed with just mongoUser
      // Other users wait for both TanStack AND Realm logins to complete

      //
      // - - - - - - - - - - REALM QUERIES
      //
      setSchedulePreparing(true);

      // ============ DEV ONLY: Simulate slow network ============
      // TO ENABLE: Uncomment this block and comment out the original queries below
      // const DEV_QUERY_DELAY_MS = 10000; // 10 seconds - set to 0 to disable
      // const delayedQuery = (queryFn, ...args) => {
      //   if (DEV_QUERY_DELAY_MS > 0) {
      //     console.log(`%c[DEV] Delaying query by ${DEV_QUERY_DELAY_MS}ms...`, "color: orange; font-weight: bold;");
      //     setTimeout(() => queryFn(...args), DEV_QUERY_DELAY_MS);
      //   } else {
      //     queryFn(...args);
      //   }
      // };
      // delayedQuery(getUniqueLocations, companyCode);
      // delayedQuery(getOfficeMetaDataByLocation, locationFilter);
      // delayedQuery(getCandidatesByCompany, companyCode, locationFilter);
      // delayedQuery(getProjectsByCompany, companyCode, locationFilter);
      // delayedQuery(getAttributesByLocation, companyCode, locationFilter);
      // delayedQuery(getUsers);
      // ============ END DEV ONLY ============

      // Realm queries removed - Realm EOL Jan 13, 2026
      // All users now use TanStack exclusively

      //
      // - - - - - - - - - - TANSTACK QUERIES
      //
      {
        // Define which queries we expect to complete for this load
        const initialExpectedQueries = new Set([
          "user", // getUserById - runs before onScheduleNeedsModel but is part of initial load
          "uniqueLocations",
          "officeMetaData",
          "projectsBeforeMerge",
          "candidatesList",
          "attributes",
          // Note: projectsAfterMerge is added dynamically when shared installers are detected
          // candidatesAdditional and projectsAdditional are conditional (not compared separately)
        ]);

        // Conditionally add users and logs if user has access
        if (screenAccess?.includes("userList")) {
          initialExpectedQueries.add("users");
        }
        if (screenAccess?.includes("logs")) {
          initialExpectedQueries.add("logs");
        }

        console.log(
          "%cScheduleContext onScheduleNeedsModel - Setting expected queries:",
          "color: #00FFFF; font-weight: bold; background-color: #1a1a1a; padding: 2px 4px;",
          [...initialExpectedQueries]
        );
        setExpectedQueries(initialExpectedQueries);

        awsgwGetUniqueLocations(companyCode);
        awsgwGetOfficeMetaDataByLocation(locationFilter);
        if (screenAccess?.includes("userList")) {
          awsgwGetUsers();
        }
        awsgwGetProjectsByCompany();
        awsgwGetCandidatesByLocation();
        awsgwGetAttributesByLocation();
        if (screenAccess?.includes("logs")) {
          awsgwGetLogs();
        }
      }
      //
      // - - - - - - - - - - <END> TANSTACK QUERIES
      //

      // DISABLED: setUserPrefsById is now handled by useEffect in modelTS.context
      // setUserPrefsById(mongoUser.id);
    } else {
      console.log(
        "ScheduleContext onScheduleNeedsModel, NO user so not proceeding with model loading..."
      );
    }
  };

  // - - - - - - - - - -

  /**
   * Pure-ish builder: takes weekDatesArray as a parameter (instead of
   * closing over AppContext's value) so each grid in the multi-grid
   * Optimizer (#105) can compute its own scheduleArray for its own
   * visible week. Returns the combined array — no setState side effects.
   *
   * All other dependencies (filteredProjectsList, candidatesList,
   * attributesList, locationFilter, dataFormatting_*, etc.) are still
   * read from the surrounding closure since those are global per location.
   *
   * The thin createScheduleArray wrapper below preserves the historical
   * behavior of writing to scheduleArray state and clearing the loading
   * spinner — used by the existing useEffects that key off
   * AppContext.weekDatesArray.
   */
  const buildScheduleArrayForWeek = (weekDatesArray) => {
    // Model received, now transform it
    // added much later: weekdaysArray has changed (user changed week) so re-create and insert in availability defaults
    //

    function convertTimeRange(str) {
      // 2025-08-05 - created by COPILOT CHAT
      switch (str) {
        case "Morning":
          return "8-11";

        case "Afternoon":
          return "11-2";

        case "Evening":
          return "2-5";

        default:
          // console.log(
          //   "ScheduleContext createScheduleArray convertTimeRange DEFAULT str: ",
          //   str
          // );
          const match = str.match(/(\d+)\s*[AP]M\s*-\s*(\d+)\s*[AP]M/i);
          if (match) {
            return `${match[1]}-${match[2]}`;
          }
          return str; // fallback if format doesn't match
      }
      // Example input: "8 AM - 5 PM"
      // const match = str.match(/(\d+)\s*[AP]M\s*-\s*(\d+)\s*[AP]M/i);
      // if (match) {
      //   return `${match[1]}-${match[2]}`;
      // }
      // return str; // fallback if format doesn't match
    }

    // loop thru filtered documents
    const newScheduleArray = filteredProjectsList.map((document, index) => {
      var newJob = {};
      // calculate material type qty (yds or sqft)

      let materialsCount = document.Materials ? document.Materials.length : 0;
      var materialsIndex = 0;
      var currentQty = 0;
      while (materialsCount > materialsIndex) {
        // loop thru materials
        const originalQty = currentQty;
        const currentMaterial = document.Materials[materialsIndex];
        const currentMaterialType = currentMaterial["KI_AQ » Materials::type"];
        // const currentMaterialQty = parseFloat(
        //   currentMaterial["KI_AQ » Materials::qty"]
        // ).toFixed(1);
        const currentMaterialYds = parseFloat(
          currentMaterial["KI_AQ » Materials::total_yds"]
        ).toFixed(0);
        const currentMaterialSqFt = parseFloat(
          currentMaterial["KI_AQ » Materials::total_sqft"]
        ).toFixed(0);
        switch (currentMaterialType) {
          case "Carpet":
            // NOTE: The unary plus operator (+) precedes its operand and evaluates to its operand but attempts to convert it into a number, if it isn't already.
            currentQty += +currentMaterialYds;
            break;
          case "Vinyl":
            currentQty += +currentMaterialSqFt;
            break;
          case "Plank":
            currentQty += +currentMaterialSqFt;
            break;
          case "Ceramic":
            currentQty += +currentMaterialSqFt;
            break;
          case "Wood":
            currentQty += +currentMaterialSqFt;
            break;
          case "Backsplash":
            currentQty += +currentMaterialSqFt;
            break;
          case "InteriorDoor":
            currentQty += +currentMaterialSqFt;
            break;
          case "ExteriorDoor":
            currentQty += +currentMaterialSqFt;
            break;
          case "StormDoor":
            currentQty += +currentMaterialSqFt;
            break;
          case "Window":
            currentQty += +currentMaterialSqFt;
            break;
          case "Pad":
            break;
          default:
            currentQty = originalQty;
        }
        materialsIndex += 1;
      }

      // calculate surfaces (for arrays) using SKU lists
      const lineItemsCount = document.InvoiceLineItems
        ? document.InvoiceLineItems.length
        : 0;
      var lineItemsIndex = 0;
      var currentFurniture = false;
      // // var currentFloating = false;
      // // var currentGlueDown = false;
      // // var currentNailDown = false;
      // // var currentTile = false;
      // // var currentBacksplash = false;
      // // var currentSheetVinyl = false;
      while (lineItemsCount > lineItemsIndex) {
        // loop thru lineItems
        const currentLineItem = document.InvoiceLineItems[lineItemsIndex];
        const currentLineItemSku =
          currentLineItem["KI_AQ » InvoiceLineItems::sku"];

        // calculate Furniture
        if (JOB_FURNITURE_SKULIST.includes(parseInt(currentLineItemSku))) {
          currentFurniture = true;
          // console.log(
          //   "ScheduleContext createScheduleArray, lineItem SKU matched furnitureSkuList, sku: ",
          //   currentLineItemSku,
          //   ", invoceNo: ",
          //   document.invoiceNo
          // );
        }

        // // calculate Floating
        // if (JOB_FLOATING_SKULIST.includes(parseInt(currentLineItemSku))) {
        //   currentFloating = true;
        //   // console.log(
        //   //   "ScheduleContext createScheduleArray, lineItem SKU matched floatingSkuList, sku: ",
        //   //   currentLineItemSku,
        //   //   ", invoceNo: ",
        //   //   document.invoiceNo
        //   // );
        // }

        // // calculate GlueDown
        // if (JOB_GLUEDOWN_SKULIST.includes(parseInt(currentLineItemSku))) {
        //   currentGlueDown = true;
        //   // console.log(
        //   //   "ScheduleContext createScheduleArray, lineItem SKU matched glueDownSkuList, sku: ",
        //   //   currentLineItemSku,
        //   //   ", invoceNo: ",
        //   //   document.invoiceNo
        //   // );
        // }

        // // calculate NailDown
        // if (JOB_NAILDOWN_SKULIST.includes(parseInt(currentLineItemSku))) {
        //   currentNailDown = true;
        //   // console.log(
        //   //   "ScheduleContext createScheduleArray, lineItem SKU matched nailDownSkuList, sku: ",
        //   //   currentLineItemSku,
        //   //   ", invoceNo: ",
        //   //   document.invoiceNo
        //   // );
        // }

        // // calculate Tile
        // if (JOB_TILE_SKULIST.includes(parseInt(currentLineItemSku))) {
        //   currentTile = true;
        //   // console.log(
        //   //   "ScheduleContext createScheduleArray, lineItem SKU matched tileSkuList, sku: ",
        //   //   currentLineItemSku,
        //   //   ", invoceNo: ",
        //   //   document.invoiceNo
        //   // );
        // }

        // // calculate Backsplash
        // if (JOB_BACKSPLASH_SKULIST.includes(parseInt(currentLineItemSku))) {
        //   currentBacksplash = true;
        //   // console.log(
        //   //   "ScheduleContext createScheduleArray, lineItem SKU matched backsplashSkuList, sku: ",
        //   //   currentLineItemSku,
        //   //   ", invoceNo: ",
        //   //   document.invoiceNo
        //   // );
        // }

        // // calculate SheetVinyl
        // if (JOB_SHEETVINYL_SKULIST.includes(parseInt(currentLineItemSku))) {
        //   currentSheetVinyl = true;
        //   // console.log(
        //   //   "ScheduleContext createScheduleArray, lineItem SKU matched furnitureSkuList, sku: ",
        //   //   currentLineItemSku,
        //   //   ", invoceNo: ",
        //   //   document.invoiceNo
        //   // );
        // }

        lineItemsIndex += 1;
      }

      // compose final object
      newJob.id = document.id;
      // Time Machine #114 Phase 5: time-travel projects come straight from
      // the Lambda with `_id` stringified but no `mongoId` (only the live
      // TanStack select adds that field). Fall back to `_id` so newJob
      // carries the MongoDB ObjectId hex in both modes — used as the
      // documentId for getEventLogByDocumentId in TimeMachineJobModal.
      newJob.mongoId =
        document.mongoId ||
        (typeof document._id === "string" ? document._id : null);
      newJob.companyCode = document.companyCode;
      newJob.location = document.location;
      newJob.invoiceNo = document.invoiceNo;
      // Time Machine #114 Phase 6 — keller ID of the customer-record. Source
      // for the JobsDetailModal History feed's BlackoutDatesAudit lookup
      // (#112 Lambda audits records keyed by inputs.kellerId === _kf_KellerID).
      // Live-mode projection in api_mongoDB_queries Lambda doesn't include
      // _kf_KellerID, so this will be empty string in live mode — but the
      // History feed only renders during time-travel, where the Lambda
      // returns full reconstructed docs that DO carry _kf_KellerID.
      newJob.kellerId = document._kf_KellerID || "";
      newJob.projectNo = document.lowes_projectNo;
      // date_scheduled in ActiveQueue:      If ( $classification = "Detail"; KellerInteriors::EstimateScheduledDate ; KellerInteriors::ScheduledDate )
      newJob.start = document.date_scheduled
        ? dataFormatting_dateFields(document.date_scheduled)
        : "";
      // date_estimatedCompletion in ActiveQueue:      If ( $classification = "Detail"; "" ; KellerInteriors::EstimatedCompletionDate )
      newJob.end = document.date_estimatedCompletion
        ? dataFormatting_dateFields(document.date_estimatedCompletion)
        : newJob.start;
      newJob.arrival = document.arrival_time_text
        ? document.arrival_time_text
        : ""; // FM field is from ActiveQueue, which has no "arrivalTime" field
      newJob.encodedArrival = document.arrival_time_text
        ? convertTimeRange(document.arrival_time_text)
        : "";
      newJob.dateDue = document.date_due ? document.date_due : "";
      newJob.classification = document.classification;
      newJob.storeNo = document.lowes_storeNo;
      newJob.surface = document.surface;
      newJob.statusCode = document.statusCode;
      newJob.qty = currentQty;
      newJob.firstName = document.customer_firstName;
      newJob.lastName = document.customer_lastName;
      newJob.cellPhone = document.customer_cellPhone;
      newJob.address = document.customer_address;
      // installer in ActiveQueue:           If ( $classification = "Detail"; KellerInteriors::Detailer: ; KellerInteriors::Installer: )
      newJob.installer =
        document.installer !== ""
          ? dataFormatting_payrollFields(document.installer)
          : "";
      newJob.expressShip = document.expressShipFlag;
      newJob.furniture = currentFurniture;
      // newJob.sheetVinyl = currentSheetVinyl;
      // newJob.floating = currentFloating;
      // newJob.glueDown = currentGlueDown;
      // newJob.nailDown = currentNailDown;
      // newJob.tile = currentTile;
      // newJob.backsplash = currentBacksplash;
      // If ( customerPortal_Scheduling > 0 (=1 means text has been sent) and portalState ≥ 300) then customer has prescheduled
      //       decided to drop custPort_Scheduling, since portalState is the better qualifier
      //    portalState = 100 : text sent, Welcome screen
      //    portalState = 200 : cust sees Calendar chooser
      //    portalState = 300 : cust has scheduled job
      // 2025-01-15 - changed RUSH from date_arranged to _autoGeneratedModificationTimestamp
      // 2025-04-16 - added KellerInteriors::Warehouse Order Complete: field so that dates in this field removes the prescheduled emoji/icon (see SUP-4271)
      // 2025-10-16 - removed portalState field so that ALL jobs without an RTS date get flagged as prescheduled, per Scrum mtg 10-15-25 (see SW-927)
      // 2026-01-22 - changed formula for "rush" (lightning bolt emoji):
      //              now defined as prescheduled, no longer caring about material received yet (RTS date), and not verified by staff
      // 2026-01-23 - modified prescheduled calc so that materialReceived (RTS date) removes prescheduled status once again
      // 2026-02-04 - looks like merging from several branches removed changes!!
      //              prescheduled ( 📅 ) = notMaterialReceived REGARDLESS of who scheduled it
      newJob.notMaterialReceived = document["KellerInteriors::Warehouse Order Complete:"] === ""
          ? true
          : false;
      newJob.notScheduledByStaff = document.portalState >= 300
          ? true
          : false;
      newJob.prescheduled = newJob.notMaterialReceived // see note above for 2026-02-04
          ? true
          : false;
      newJob.notVerifiedByStaff = document["KellerInteriors::_autoGeneratedModificationTimestamp"] === ""
          ? true
          : false;
      // rush = regardless of material received, prescheduled but not finalized/verified by staff
      newJob.rush = newJob.notScheduledByStaff && newJob.notVerifiedByStaff
          ? true
          : false;
      newJob.deliveryDate = document.deliveryDate || "";
      newJob.deliveryWindow = document.deliveryWindow || "";
      newJob.thanksgivingRush = document.thanksgivingRushFlag;
      newJob.christmasRush = document.christmasRushFlag;
      // if scheduled Job, and a required field is empty, show IMS warning
      newJob.imsWarning =
        document.classification !== "Detail" &&
        document.date_scheduled !== "" &&
        (document.date_estimatedCompletion === "" ||
          newJob.arrival === "" ||
          document.lowes_projectNo === "" ||
          document.installer === "")
          ? true
          : false;

      // add Job-specific Attributes, if they exist (primary key is invoiceNo instead of _kf_payrollNo)
      // Use String() comparison to handle type mismatch (invoiceNo can be string or number)
      // This prevents duplicate attributes from being created when types don't match
      newJob.jobAttributes = attributesList.find(
        (obj) => String(obj.invoiceNo) === String(document.invoiceNo) && obj.invoiceNo !== 0 && obj.invoiceNo !== ""
      );

      // ActiveQueue flags take precedence over Attributes (#78, #81)
      // FileMaker may update these flags directly on ActiveQueue documents
      // Only override on EXISTING jobAttributes — do not create a fake object without _id,
      // as that would cause the Attributes mutation in JobsDetailModal to fail silently
      if (newJob.jobAttributes) {
        if (document.expediteFlag !== undefined) {
          newJob.jobAttributes.expedite = document.expediteFlag;
        }
        if (document.distantFlag !== undefined) {
          newJob.jobAttributes.distant = document.distantFlag;
        }
      }

      newJob.flags = `${newJob.thanksgivingRush === 1 ? "🎃" : ""}${
        newJob.christmasRush === 1 ? "🎄" : ""
      }${newJob.expressShip === 1 ? "📦" : ""}${newJob.furniture ? "🪑" : ""}${
        newJob.prescheduled ? "📅" : ""
      }${newJob.rush ? "⚡️" : ""}${
        newJob.jobAttributes?.expedite ? "🏃🏽‍♂️" : ""
      }${newJob.jobAttributes?.distant ? "✈️" : ""}${
        newJob.imsWarning ? "🛑" : ""
      }`;

      // newJob.encodedSmallTitle = dataFormatting_encodedSmallTitle(newJob);
      newJob.encodedSmallTitle = dataFormatting_encodedSmallTitleNew(
        newJob,
        jobTitleConfig
      );
      // newJob.encodedLargeTitle = dataFormatting_encodedLargeTitle(newJob);
      newJob.encodedLargeTitle = dataFormatting_encodedLargeTitleNew(
        newJob,
        jobTitleConfig
      );
      newJob.index = index;
      newJob.extraCellId = document.extraCellId;
      newJob.extraCellText = document.extraCellText;

      newJob.selected = false;

      return newJob;
    }); // filteredProjectsList.map
    // console.log("ScheduleContext createScheduleArray: ", newScheduleArray);

    // add Availability Defaults Attribute data to scheduleArray
    const newDefaultArray = [];
    const defaultAttributeList = attributesList.filter(
      (doc) =>
        doc._kf_payrollNo !== "" &&
        doc.date === "" &&
        doc.availability.length > 0
    );
    if (defaultAttributeList?.length > 0) {
      console.log(
        `ScheduleContext defaultAttributeList(${defaultAttributeList.length}): `,
        defaultAttributeList
      );
      defaultAttributeList.forEach((attributeDoc, attributeIndex) => {
        const defaultAttributeObject = attributeDoc;
        const defaultsArray = defaultAttributeObject.availability;
        const daysAffected = defaultsArray.length;
        // console.log(
        //   `ScheduleContext defaultAttributeObject: `,
        //   defaultAttributeObject
        // );
        if (daysAffected > 0) {
          // this attribute will NOT be added to the Attributes Collection
          //   therefore no DOCUMENT_ATTRIBUTES_TEMPLATE (keys.js) template will be used
          var newAttribute = {};
          var newAttributeTemplate = {};
          newAttributeTemplate.companyCode = defaultAttributeObject.companyCode;
          newAttributeTemplate.location = defaultAttributeObject.location;
          newAttributeTemplate.installer =
            defaultAttributeObject.installer !== ""
              ? dataFormatting_payrollFields(
                  defaultAttributeObject._kf_payrollNo
                )
              : "";
          newAttributeTemplate.start = "";
          newAttributeTemplate.end = "";
          newAttributeTemplate.id = createUuid();
          newAttributeTemplate.invoiceNo = "";
          newAttributeTemplate.encodedLargeTitle = "";
          newAttributeTemplate.encodedSmallTitle = "";
          newAttributeTemplate.locked = false;
          newAttributeTemplate.distant = false;
          newAttributeTemplate.expedite = false;
          newAttributeTemplate.attribute = true;
          newAttributeTemplate.availability = defaultsArray;

          // 2025-02-11 - stopped removing attribute if a job was assigned; default attributes are now always present
          weekDatesArray.forEach((date, index) => {
            switch (index) {
              case 0:
                if (defaultsArray.find((day) => day.Mon)) {
                  // if (
                  //   newScheduleArray.find(
                  //     (job) =>
                  //       job.start === date &&
                  //       job.installer === newAttributeTemplate.installer
                  //   )
                  // ) {
                  //   // don't create a default attribute if a job has already been assigned
                  // } else {
                  newAttribute = { ...newAttributeTemplate };
                  newAttribute.start = date;
                  newAttribute.end = date;
                  newAttribute.encodedSmallTitle = defaultsArray.find(
                    (day) => day.Mon
                  ).Mon;
                  newAttribute.encodedLargeTitle = defaultsArray.find(
                    (day) => day.Mon
                  ).Mon;
                  newDefaultArray.push(newAttribute);
                  // }
                }
                break;

              case 1:
                if (defaultsArray.find((day) => day.Tue)) {
                  // if (
                  //   newScheduleArray.find(
                  //     (job) =>
                  //       job.start === date &&
                  //       job.installer === newAttributeTemplate.installer
                  //   )
                  // ) {
                  //   // don't create a default attribute if a job has already been assigned
                  // } else {
                  newAttribute = { ...newAttributeTemplate };
                  newAttribute.start = date;
                  newAttribute.end = date;
                  newAttribute.encodedSmallTitle = defaultsArray.find(
                    (day) => day.Tue
                  ).Tue;
                  newAttribute.encodedLargeTitle = defaultsArray.find(
                    (day) => day.Tue
                  ).Tue;
                  newDefaultArray.push(newAttribute);
                  // }
                }
                break;

              case 2:
                if (defaultsArray.find((day) => day.Wed)) {
                  // if (
                  //   newScheduleArray.find(
                  //     (job) =>
                  //       job.start === date &&
                  //       job.installer === newAttributeTemplate.installer
                  //   )
                  // ) {
                  //   // don't create a default attribute if a job has already been assigned
                  // } else {
                  newAttribute = { ...newAttributeTemplate };
                  newAttribute.start = date;
                  newAttribute.end = date;
                  newAttribute.encodedSmallTitle = defaultsArray.find(
                    (day) => day.Wed
                  ).Wed;
                  newAttribute.encodedLargeTitle = defaultsArray.find(
                    (day) => day.Wed
                  ).Wed;
                  newDefaultArray.push(newAttribute);
                  // }
                }
                break;

              case 3:
                if (defaultsArray.find((day) => day.Thr)) {
                  // if (
                  //   newScheduleArray.find(
                  //     (job) =>
                  //       job.start === date &&
                  //       job.installer === newAttributeTemplate.installer
                  //   )
                  // ) {
                  //   // don't create a default attribute if a job has already been assigned
                  // } else {
                  newAttribute = { ...newAttributeTemplate };
                  newAttribute.start = date;
                  newAttribute.end = date;
                  newAttribute.encodedSmallTitle = defaultsArray.find(
                    (day) => day.Thr
                  ).Thr;
                  newAttribute.encodedLargeTitle = defaultsArray.find(
                    (day) => day.Thr
                  ).Thr;
                  newDefaultArray.push(newAttribute);
                  // }
                }
                break;

              case 4:
                if (defaultsArray.find((day) => day.Fri)) {
                  // if (
                  //   newScheduleArray.find(
                  //     (job) =>
                  //       job.start === date &&
                  //       job.installer === newAttributeTemplate.installer
                  //   )
                  // ) {
                  //   // don't create a default attribute if a job has already been assigned
                  // } else {
                  newAttribute = { ...newAttributeTemplate };
                  newAttribute.start = date;
                  newAttribute.end = date;
                  newAttribute.encodedSmallTitle = defaultsArray.find(
                    (day) => day.Fri
                  ).Fri;
                  newAttribute.encodedLargeTitle = defaultsArray.find(
                    (day) => day.Fri
                  ).Fri;
                  newDefaultArray.push(newAttribute);
                  // }
                }
                break;

              case 5:
                if (defaultsArray.find((day) => day.Sat)) {
                  // if (
                  //   newScheduleArray.find(
                  //     (job) =>
                  //       job.start === date &&
                  //       job.installer === newAttributeTemplate.installer
                  //   )
                  // ) {
                  //   // don't create a default attribute if a job has already been assigned
                  // } else {
                  newAttribute = { ...newAttributeTemplate };
                  newAttribute.start = date;
                  newAttribute.end = date;
                  newAttribute.encodedSmallTitle = defaultsArray.find(
                    (day) => day.Sat
                  ).Sat;
                  newAttribute.encodedLargeTitle = defaultsArray.find(
                    (day) => day.Sat
                  ).Sat;
                  newDefaultArray.push(newAttribute);
                  // }
                }
                break;

              case 6:
                if (defaultsArray.find((day) => day.Sun)) {
                  // if (
                  //   newScheduleArray.find(
                  //     (job) =>
                  //       job.start === date &&
                  //       job.installer === newAttributeTemplate.installer
                  //   )
                  // ) {
                  //   // don't create a default attribute if a job has already been assigned
                  // } else {
                  newAttribute = { ...newAttributeTemplate };
                  newAttribute.start = date;
                  newAttribute.end = date;
                  newAttribute.encodedSmallTitle = defaultsArray.find(
                    (day) => day.Sun
                  ).Sun;
                  newAttribute.encodedLargeTitle = defaultsArray.find(
                    (day) => day.Sun
                  ).Sun;
                  newDefaultArray.push(newAttribute);
                  // }
                }
                break;

              default:
                break;
            }
          });
        }
      });
      //
    } else {
      console.log(
        `ScheduleContext defaultAttributeList(0): `,
        defaultAttributeList
      );
    } // if (defaultAttributeObject?.length > 0)
    console.log(`ScheduleContext newDefaultArray: `, newDefaultArray);

    // add Attributes data to scheduleArray
    const newAttributeArray = attributesList
      .filter((document, index) => {
        // filter out Job-specific Attributes (primary key is invoiceNo instead of _kf_payrollNo)
        // filter out availabilityDefaults (date="")
        // only capture single date availabilty entries
        return document._kf_payrollNo !== "" && document.date !== "";
      })
      .map((document, index) => {
        var newJob = {};
        newJob._id = document._id;
        newJob.id = document.id;
        newJob.companyCode = document.companyCode;
        newJob.location = document.location;
        // NOTE: since Attribute Docs will now handle Installer/Date Docs AND Job Docs
        //          we want to hide Docs without payrollNos from schedule Cells
        //          so to do that start & installer has to be blank
        //          If only installer is blank then the date causes it to appear in Unassigned row
        newJob.start =
          document.date && document._kf_payrollNo !== ""
            ? dataFormatting_dateFields(document.date)
            : "";
        newJob.end = newJob.start;
        newJob.installer =
          document._kf_payrollNo !== ""
            ? dataFormatting_payrollFields(document._kf_payrollNo)
            : "";
        newJob.encodedSmallTitle = document.encodedSmallTitle;
        newJob.encodedLargeTitle = document.encodedLargeTitle;
        // newJob.distant = false; // not used here in payroll Attributes
        // newJob.expedite = false; // not used here in payroll Attributes
        newJob.locked = document.locked;
        newJob.availability = [];
        newJob.index = index;
        newJob.attribute = true; // added for UI (JobTile), now that old attributes.json blob has been deprecated
        return newJob;
      });
    console.log("ScheduleContext newAttributeArray: ", newAttributeArray);

    // add Extra Cell (from Attributes Collection) data to scheduleArray
    const newExtraArray = attributesList
      .filter((document, index) => {
        // filter out Job-specific Attributes (primary key is invoiceNo instead of _kf_payrollNo)
        // filter out availabilityDefaults (date="")
        // only capture entries with the "extra" object
        return document.extra && document.date !== "";
      })
      .map((document, index) => {
        var newJob = {};
        newJob._id = document._id;
        newJob.id = document.id;
        newJob.companyCode = document.companyCode;
        newJob.location = document.location;
        newJob.start = dataFormatting_dateFields(document.date);
        newJob.end = newJob.start;
        newJob.installer = "";
        newJob.encodedSmallTitle = document.extra?.customText;
        newJob.encodedLargeTitle = document.extra?.customText;
        newJob.surface = document.extra?.surface;
        newJob.distant = false; // not used here in payroll Attributes
        newJob.expedite = false; // not used here in payroll Attributes
        newJob.locked = document.locked;
        // newJob.availability = document.availability;
        newJob.index = index;
        newJob.attribute = true; // added for UI (JobTile), now that old attributes.json blob has been deprecated
        newJob.extraCell = true; // added for UI (JobTile)
        return newJob;
      });
    // console.log("ScheduleContext newAttributeArray: ", newAttributeArray);

    // 2025-01-28 - Compliance Hold indicators for installers with Scheduling_Hold_Effective_Date
    const newComplianceHoldArray = [];
    candidatesList.forEach((candidate) => {
      if (candidate.Scheduling_Hold_Effective_Date) {
        const effectiveDate = new Date(candidate.Scheduling_Hold_Effective_Date);
        effectiveDate.setHours(0, 0, 0, 0);
        const payrollNo = dataFormatting_payrollFields(candidate.payrollNo);

        weekDatesArray.forEach((dateStr) => {
          const cellDate = new Date(dateStr);
          cellDate.setHours(0, 0, 0, 0);

          // Show compliance hold indicator on/after effective date
          if (cellDate >= effectiveDate) {
            newComplianceHoldArray.push({
              id: `compliance-${payrollNo}-${dateStr}`,
              companyCode: candidate.companyCode,
              location: candidate.cLocationCode,
              installer: payrollNo,
              start: dateStr,
              end: dateStr,
              encodedSmallTitle: "🔥 compliance hold 🔥",
              encodedLargeTitle: "🔥 compliance hold 🔥",
              locked: true,
              attribute: true,
              complianceHold: true,
            });
          }
        });
      }
    });
    console.log(
      "ScheduleContext newComplianceHoldArray: ",
      newComplianceHoldArray
    );

    // finalize data
    const combinedArray = [
      ...newScheduleArray,
      ...newAttributeArray,
      ...newDefaultArray,
      ...newExtraArray,
      ...newComplianceHoldArray,
    ];
    console.log(
      `ScheduleContext combinedArray new ScheduleArray (week of ${weekDatesArray[0]}): `,
      combinedArray
    );
    return combinedArray;
  };

  // - - - - - - - - - -

  /**
   * Thin wrapper that preserves the legacy behavior of createScheduleArray:
   * builds the array for AppContext.weekDatesArray and writes to scheduleArray
   * state. Side-effect of clearing the loading spinner stays here, where it
   * always lived. Existing useEffects continue to call this with no signature
   * change.
   */
  const createScheduleArray = () => {
    const combinedArray = buildScheduleArrayForWeek(weekDatesArray);
    setScheduleArray(combinedArray);
    // Only turn off loading indicator if we actually have data (crews loaded)
    // This prevents premature hiding during slow queries
    if (filteredProjectsList.length > 0 || candidatesList.length > 0) {
      setSchedulePreparing(false);
    }
  };

  // - - - - - - - - - -

  /**
   * Pure-ish builder for the counts-per-day array (#105 phase 4a).
   * Same pattern as buildScheduleArrayForWeek: weekDatesArray as a param,
   * other deps from closure, returns the new array.
   */
  const buildCountsArrayForWeek = (weekDatesArray) => {
    var newCountsArray = weekDatesArray.map((date, index) => {
      var newCountObject = {
        date: date,
        softJobs: 0,
        softSlots: 0,
        softFilledSlots: new Set(),
        hardJobs: 0,
        hardSlots: 0,
        hardFilledSlots: new Set(),
      };
      return newCountObject;
    });

    // loop thru unfiltered project documents
    projectsList.forEach((document, projectIndex) => {
      const classification = document.classification;
      const start =
        document.date_scheduled !== ""
          ? dataFormatting_dateFields(document.date_scheduled)
          : "";
      var end =
        document.date_estimatedCompletion !== ""
          ? dataFormatting_dateFields(document.date_estimatedCompletion)
          : "";
      if (end === "") {
        end = start;
      }
      const installer =
        document.installer !== ""
          ? dataFormatting_payrollFields(document.installer)
          : "";

      if (start !== "") {
        // loop thru columns to determine which is applicable
        weekDatesArray.forEach((date, dateIndex) => {
          if (
            (new Date(start).getTime() >= new Date(date).getTime() &&
              new Date(end).getTime() <= new Date(date).getTime()) ||
            (new Date(date).getTime() >= new Date(start).getTime() &&
              new Date(date).getTime() <= new Date(end).getTime())
          ) {
            if (installer !== "") {
              // find applicable installer document
              const matchedCandidate = candidatesList.filter((candidate) => {
                const formattedPayrollNo = dataFormatting_payrollFields(
                  candidate.payrollNo
                );
                return formattedPayrollNo === installer;
              });
              if (matchedCandidate.length > 0) {
                // determine which surface type to increment applicable count
                if (
                  matchedCandidate[0].Scheduling_Surface === "Soft Surface" &&
                  classification !== "Detail"
                ) {
                  newCountsArray[dateIndex].softJobs += 1;
                  newCountsArray[dateIndex].softFilledSlots.add(
                    document.installer
                  );
                } else if (
                  matchedCandidate[0].Scheduling_Surface === "Hard Surface" &&
                  classification !== "Detail"
                ) {
                  newCountsArray[dateIndex].hardJobs += 1;
                  newCountsArray[dateIndex].hardFilledSlots.add(
                    document.installer
                  );
                }
              }
            } else {
              // installer == "" => Unassigned Scheduled
              switch (document.surface) {
                case "Carpet":
                  newCountsArray[dateIndex].softJobs += 1;
                  break;
                case "Vinyl":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "Plank":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "Ceramic":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "Wood":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "Backsplash":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "InteriorDoor":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "ExteriorDoor":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "StormDoor":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "Window":
                  newCountsArray[dateIndex].hardJobs += 1;
                  break;
                case "Pad":
                  break;
                default:
                  break;
              }
            }
          }
        });
      }
    });

    // loop thru candidates
    candidatesList.forEach((document, candidateIndex) => {
      const surface = document.Scheduling_Surface;
      if (surface === "Soft Surface") {
        weekDatesArray.forEach((date, dateIndex) => {
          newCountsArray[dateIndex].softSlots += 1;
        });
      } else if (surface === "Hard Surface") {
        weekDatesArray.forEach((date, dateIndex) => {
          newCountsArray[dateIndex].hardSlots += 1;
        });
      }
    });

    // console.log("ScheduleContext createCountsArray: ", newCountsArray);
    return newCountsArray;
  };

  // - - - - - - - - - -

  /**
   * Thin wrapper that preserves legacy createCountsArray behavior — builds
   * for AppContext.weekDatesArray and writes to countsArray state. Existing
   * useEffects + processDragNDrop continue to call this with no signature
   * change.
   */
  const createCountsArray = () => {
    const newCountsArray = buildCountsArrayForWeek(weekDatesArray);
    setCountsArray(newCountsArray);
  };

  // - - - - - - - - - -

  /**
   * Creates the furniture array for scheduling.
   */
  const createFurnitureArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newFurnitureArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newFurnitureObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newFurnitureArray.push(newFurnitureObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Soft Surface") {
          // are they enabled for this surface in the Soft section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_carpet_once_furniture > 0) {
              // yes, they are enabled for this surface
              newFurnitureArray[dateIndex].softSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createFurnitureArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY attribute (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo
                  // 2025-02-11 - removed this condition to allow for all 3 attributes OFF/BUFFER/🔥 to be counted as filled
                  // (document.encodedSmallTitle === "OFF" ||
                  //   document.encodedSmallTitle === "BUFFER" ||
                  //   document.encodedSmallTitle[0] === "🔥")
                );
              });
              // console.log(
              //   "ScheduleContext createFurnitureArray SOFT query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newFurnitureArray[dateIndex].softSurface.filled += 1;
              }
            }
          }
          //
        } else if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (
              document.capacity_vinyl_once_furniture > 0 ||
              document.capacity_vinylSheet_once_furniture > 0 ||
              document.capacity_tile_once_furniture > 0 ||
              document.capacity_backsplash_once_furniture > 0 ||
              document.capacity_floating_once_furniture > 0 ||
              document.capacity_glueDown_once_furniture > 0 ||
              document.capacity_nailDown_once_furniture > 0
            ) {
              // yes, they are enabled for this surface
              newFurnitureArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createFurnitureArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY attribute (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo
                  // 2025-02-11 - removed this condition to allow for all 3 attributes OFF/BUFFER/🔥 to be counted as filled
                  // (document.encodedSmallTitle === "OFF" ||
                  //   document.encodedSmallTitle === "BUFFER" ||
                  //   document.encodedSmallTitle[0] === "🔥")
                );
              });
              // console.log(
              //   "ScheduleContext createFurnitureArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newFurnitureArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }
      // console.log(
      //   "ScheduleContext newFurnitureArray (after CandidatesList): ",
      //   newFurnitureArray
      // );

      // console.log("ScheduleContext newFurnitureArray: ", newFurnitureArray);
      setFurnitureArray(newFurnitureArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the floating array for scheduling.
   */
  const createFloatingArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newFloatingArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newFloatingObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newFloatingArray.push(newFloatingObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Soft Surface") {
          // are they enabled for this surface in the Soft section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_floating_once > 0) {
              // yes, they are enabled for this surface
              newFloatingArray[dateIndex].softSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createFloatingArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createFloatingArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newFloatingArray[dateIndex].softSurface.filled += 1;
              }
            }
          }
          //
        } else if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_floating_once > 0) {
              // yes, they are enabled for this surface
              newFloatingArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createFloatingArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createFloatingArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newFloatingArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newFloatingArray: ", newFloatingArray);
      setFloatingArray(newFloatingArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the glue down array for scheduling.
   */
  const createGlueDownArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newGlueDownArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newGlueDownObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newGlueDownArray.push(newGlueDownObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Soft Surface") {
          // are they enabled for this surface in the Soft section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_glueDown_once > 0) {
              // yes, they are enabled for this surface
              newGlueDownArray[dateIndex].softSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createGlueDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createGlueDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newGlueDownArray[dateIndex].softSurface.filled += 1;
              }
            }
          }
          //
        } else if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_glueDown_once > 0) {
              // yes, they are enabled for this surface
              newGlueDownArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createGlueDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createGlueDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newGlueDownArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newGlueDownArray: ", newGlueDownArray);
      setGlueDownArray(newGlueDownArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the nail down array for scheduling.
   */
  const createNailDownArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newNailDownArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newNailDownObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newNailDownArray.push(newNailDownObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Soft Surface") {
          // are they enabled for this surface in the Soft section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_nailDown_once > 0) {
              // yes, they are enabled for this surface
              newNailDownArray[dateIndex].softSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createNailDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createNailDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newNailDownArray[dateIndex].softSurface.filled += 1;
              }
            }
          }
          //
        } else if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_nailDown_once > 0) {
              // yes, they are enabled for this surface
              newNailDownArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createNailDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createNailDownArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newNailDownArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newNailDownArray: ", newNailDownArray);
      setNailDownArray(newNailDownArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the vinyl array for scheduling.
   */
  const createVinylArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newVinylArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newVinylObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newVinylArray.push(newVinylObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Soft Surface") {
          // are they enabled for this surface in the Soft section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_vinylSheet_once > 0) {
              // yes, they are enabled for this surface
              newVinylArray[dateIndex].softSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createVinylArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createVinylArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newVinylArray[dateIndex].softSurface.filled += 1;
              }
            }
          }
          //
        } else if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_vinylSheet_once > 0) {
              // yes, they are enabled for this surface
              newVinylArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createVinylArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createVinylArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newVinylArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newVinylArray: ", newVinylArray);
      setVinylArray(newVinylArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the tile array for scheduling.
   */
  const createTileArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newTileArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newTileObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newTileArray.push(newTileObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Soft Surface") {
          // are they enabled for this surface in the Soft section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_tile_once > 0) {
              // yes, they are enabled for this surface
              newTileArray[dateIndex].softSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createTileArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createTileArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newTileArray[dateIndex].softSurface.filled += 1;
              }
            }
          }
          //
        } else if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_tile_once > 0) {
              // yes, they are enabled for this surface
              newTileArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createTileArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createTileArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newTileArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newTileArray: ", newTileArray);
      setTileArray(newTileArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the backsplash array for scheduling.
   */
  const createBacksplashArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newBacksplashArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newBacksplashObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newBacksplashArray.push(newBacksplashObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Soft Surface") {
          // are they enabled for this surface in the Soft section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_backsplash_once > 0) {
              // yes, they are enabled for this surface
              newBacksplashArray[dateIndex].softSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createBacksplashArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createBacksplashArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newBacksplashArray[dateIndex].softSurface.filled += 1;
              }
            }
          }
          //
        } else if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_backsplash_once > 0) {
              // yes, they are enabled for this surface
              newBacksplashArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createBacksplashArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createBacksplashArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newBacksplashArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newBacksplashArray: ", newBacksplashArray);
      setBacksplashArray(newBacksplashArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the interiorDoor array for scheduling.
   */
  const createInteriorDoorArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newInteriorDoorArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newInteriorDoorObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newInteriorDoorArray.push(newInteriorDoorObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_interior_doors > 0) {
              // yes, they are enabled for this surface
              newInteriorDoorArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createInteriorDoorArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createInteriorDoorArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newInteriorDoorArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log(
      //   "ScheduleContext newInteriorDoorArray: ",
      //   newInteriorDoorArray
      // );
      setInteriorDoorArray(newInteriorDoorArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the exteriorDoor array for scheduling.
   */
  const createExteriorDoorArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newExteriorDoorArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newExteriorDoorObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newExteriorDoorArray.push(newExteriorDoorObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_exterior_doors > 0) {
              // yes, they are enabled for this surface
              newExteriorDoorArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createExteriorDoorArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createExteriorDoorArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newExteriorDoorArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      console.log(
        // "ScheduleContext newExteriorDoorArray: ",
        newExteriorDoorArray
      );
      setExteriorDoorArray(newExteriorDoorArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the stormDoor array for scheduling.
   */
  const createStormDoorArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newStormDoorArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newStormDoorObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newStormDoorArray.push(newStormDoorObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_storm_doors > 0) {
              // yes, they are enabled for this surface
              newStormDoorArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createStormDoorArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createStormDoorArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newStormDoorArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newStormDoorArray: ", newStormDoorArray);
      setStormDoorArray(newStormDoorArray);
    }
  };

  // - - - - - - - - - -

  /**
   * Creates the window array for scheduling.
   */
  const createWindowArray = () => {
    if (
      weekDatesArray.length > 0 &&
      candidatesList.length > 0 &&
      projectsList.length > 0
    ) {
      var newWindowArray = [];
      for (let index = 0; index < weekDatesArray.length; index++) {
        const date = weekDatesArray[index];
        const newWindowObject = {
          date: date,
          softSurface: {
            available: 0,
            filled: 0,
          },
          hardSurface: {
            available: 0,
            filled: 0,
          },
        };
        newWindowArray.push(newWindowObject);
      }

      // loop thru candidates
      for (
        let candidateIndex = 0;
        candidateIndex < candidatesList.length;
        candidateIndex++
      ) {
        const document = candidatesList[candidateIndex];
        const surface = document.Scheduling_Surface;
        const payrollNo = dataFormatting_payrollFields(document.payrollNo);
        if (surface === "Hard Surface") {
          // are they enabled for this surface in the Hard section
          for (
            let dateIndex = 0;
            dateIndex < weekDatesArray.length;
            dateIndex++
          ) {
            const currentDate = weekDatesArray[dateIndex];
            // loop thru week
            if (document.capacity_window > 0) {
              // yes, they are enabled for this surface
              newWindowArray[dateIndex].hardSurface.available += 1;

              // have they already been assigned ANY job (making this surface Filled)
              const assignedProject = scheduleArray.filter((document) => {
                return (
                  !document.attribute &&
                  // 2025-03019 - disabled this filter so that shared jobs from other locations will apply here   document.location === locationFilter &&
                  document.installer === payrollNo &&
                  new Date(currentDate).getTime() >=
                    new Date(document.start).getTime() &&
                  new Date(currentDate).getTime() <=
                    new Date(document.end).getTime()
                );
              });
              // console.log(
              //   "ScheduleContext createWindowArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", currentDate: ",
              //   currentDate,
              //   ", found: ",
              //   assignedProject?.length
              // );

              // have they already been assigned ANY job (making this surface Filled)
              const availability = scheduleArray.filter((document) => {
                return (
                  document.attribute === true &&
                  document.location === locationFilter &&
                  new Date(currentDate).getTime() ===
                    new Date(document.start).getTime() &&
                  document.installer === payrollNo &&
                  (document.encodedSmallTitle === "OFF" ||
                    document.encodedSmallTitle === "BUFFER")
                );
              });
              // console.log(
              //   "ScheduleContext createWindowArray query, locationFilter: ",
              //   locationFilter,
              //   ", payrollNo: ",
              //   payrollNo,
              //   ", found: ",
              //   availability?.length
              // );

              if (assignedProject?.length > 0 || availability?.length > 0) {
                newWindowArray[dateIndex].hardSurface.filled += 1;
              }
            }
          }
        }
      }

      // console.log("ScheduleContext newWindowArray: ", newWindowArray);
      setWindowArray(newWindowArray);
    }
  };

  // - - - - - - - - - - - - - - - - -  EFFECT HOOKS  - - - - - - - - - - - - - - - - - -
  //
  // companyCode change                     -> setScheduleNeedsModel
  // filteredProjectsList change            -> createScheduleArray
  // projectsList, candidatesList change    -> setScheduleNeedsModel
  // attributesList, locationArray change   -> setScheduleNeedsModel
  // scheduleArray change                   -> createCountsArray, createFurnitureArray
  // weekDatesArray change                  -> createScheduleArray
  // scheduleNeedsModel change              -> onScheduleNeedsModel
  // dropObject change                      -> openJobInitModal
  // processingDragNDrop change             -> processDragNDrop
  //

  // - - - - - - - - - -

  // Track previous locationFilter to detect changes
  const prevLocationFilterRef = useRef(locationFilter);

  useEffect(() => {
    // Phase 6 — the right-side provider does NOT own data orchestration.
    // The outer provider already runs this effect; running it again on the
    // inner provider would call onScheduleNeedsModel() (via setScheduleNeedsModel(true)
    // → useEffect[scheduleNeedsModel] cascade), which clears the shared
    // ModelTsContext result arrays (setAwsgwProjectsResult([]) etc.) and
    // breaks the LEFT grid's data ~30s after entering Diff Pair (the timing
    // of the React state-flush + setTimeout(50) below). #114 Phase 6 bug.
    if (useRightSide) return;

    const locationChanged = prevLocationFilterRef.current !== locationFilter && prevLocationFilterRef.current !== null;
    prevLocationFilterRef.current = locationFilter;

    // REALM DISABLED - 2026-01-13 (Soft removal phase)
    // All users are tanstackOnly now - no need to wait for rwMongoUser
    const canProceedWithModelLoad = !!userDocument?.id;

    if (canProceedWithModelLoad) {
      if (locationChanged) {
        // Location changed - ALWAYS force a reload, even if scheduleNeedsModel is already true
        console.log("ScheduleContext useEffect[companyCode, locationFilter, userDocument]...(location CHANGED - forcing reload)");

        // Clear data IMMEDIATELY to prevent flash of stale data during location switch
        setScheduleArray([]);
        setSchedulePreparing(true);

        // Reset to false first to ensure the useEffect[scheduleNeedsModel] fires again
        setScheduleNeedsModel(false);
        // Use setTimeout to ensure the state update is processed before setting back to true
        // Safari needs more time than Chrome for React state updates to propagate
        // and for modelTS.context.js location change cleanup to complete
        setTimeout(() => setScheduleNeedsModel(true), 50);
      } else if (!scheduleNeedsModel) {
        // Initial load or companyCode/userDocument changed - trigger model load
        console.log("ScheduleContext useEffect[companyCode, locationFilter, userDocument]...(request Model)");
        setScheduleNeedsModel(true);
      } else {
        console.log(
          "ScheduleContext useEffect[companyCode, locationFilter, userDocument], already scheduleNeedsModel, id: ",
          userDocument?.id,
          ", scheduleNeedsModel: ",
          scheduleNeedsModel
        );
      }
    } else {
      console.log(
        "ScheduleContext useEffect[companyCode, locationFilter, userDocument], waiting for user - userDoc.id: ",
        userDocument?.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode, locationFilter, userDocument]);

  // - - - - - - - - - -

  useEffect(() => {
    // filteredProjectsList has changed
    console.log(
      "ScheduleContext useEffect[filteredProjectsList]...(createScheduleArray)"
    );
    createScheduleArray();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProjectsList]);

  // - - - - - - - - - -
  // "THE BIG MOVE" useEffects removed - Realm EOL Jan 13, 2026
  // All data now comes directly from TanStack via aliases:
  //   projectsList = projectListAfterMerge
  //   candidatesList = awsgwCandidatesResult
  //   attributesList = awsgwAttributesResult
  // The useEffect above watching filteredProjectsList triggers createScheduleArray automatically

  // - - - - - - - - - -

  // "THE BIG MOVE (Attributes)" useEffect removed - Realm EOL Jan 13, 2026
  // attributesList is now an alias to awsgwAttributesResult
  // Location validation is handled by TanStack query configuration

  // - - - - - - - - - -

  useEffect(() => {
    // projectsList or candidatesList or attributesList or locationArray has updated
    // if (!crewQueryCreated && !projectsLoading && !candidatesLoading) {
    //   createCrewQueryList();
    // }
    // NOTE: schedulePreparing check ensures queries were actually started before we mark loading complete
    // Without this check, if rwMongoUser is null, no queries run but loading flags are all false
    if (
      scheduleNeedsModel &&
      schedulePreparing &&
      !projectsLoading &&
      !candidatesLoading &&
      !attributesLoading &&
      !locationsLoading
    ) {
      console.log(
        "ScheduleContext useEffect[projectsList, candidatesList, attributesList, locationArray, loading=false]...(done loading all lists)"
      );
      setScheduleNeedsModel(false);
      // createCrewQueryList removed - Realm EOL Jan 13, 2026
      // TanStack equivalent is createAdditionalCandidateQueryList
      createAdditionalCandidateQueryList();
    } else {
      console.log(
        "ScheduleContext useEffect[projectsList, candidatesList, attributesList, locationArray]...not done loading or NOT scheduleNeedsModel"
      );
    }
    createScheduleArray();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectsList,
    candidatesList,
    attributesList,
    locationArray,
    jobTitleConfig,
    schedulePreparing,
  ]);

  // - - - - - - - - - -
  // Realm-specific useEffects removed - Realm EOL Jan 13, 2026
  // crewQueryList/crewQueryLoading and projectQueryList/projectQueryLoading
  // were Realm-specific variables for secondary queries. TanStack handles
  // all data fetching now via the primary queries.

  // - - - - - - - - - -

  useEffect(() => {
    console.log(
      "ScheduleContext useEffect[scheduleArray]...(createCountsArray, create SurfaceArrays)"
    );
    createCountsArray();
    createFurnitureArray();
    createFloatingArray();
    createGlueDownArray();
    createNailDownArray();
    createVinylArray();
    createTileArray();
    createBacksplashArray();
    createInteriorDoorArray();
    createExteriorDoorArray();
    createStormDoorArray();
    createWindowArray();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleArray]);

  // - - - - - - - - - -

  useEffect(() => {
    // added this useEffect for when week changes (so that we can insert availability defaults into ScheduleArray),
    //    and removing weekDatesArray changes from above useEffect as to not duplicate
    console.log(
      "ScheduleContext useEffect[weekDatesArray]...(createScheduleArray)"
    );
    createScheduleArray();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDatesArray]);

  // - - - - - - - - - -

  useEffect(() => {
    // Phase 6 — same gate as above: onScheduleNeedsModel() is data-orchestration
    // owned by the outer provider only. Inner (right-side) provider must not
    // re-run it or the shared ModelTsContext result arrays get cleared.
    if (useRightSide) return;

    // scheduleNeedsModel has updated
    // REALM DISABLED - 2026-01-13 (Soft removal phase)
    // All users are tanstackOnly now - removed rwMongoUser dependency
    // NOTE: Removed !schedulePreparing check - if user changes location while queries are running,
    // we should start new queries for the new location. onScheduleNeedsModel handles resetting state.
    if (scheduleNeedsModel) {
      console.log(
        "ScheduleContext useEffect[scheduleNeedsModel=true]...(request Model)"
      );
      onScheduleNeedsModel();
    } else {
      console.log(
        "ScheduleContext useEffect[scheduleNeedsModel], not requesting Model; scheduleNeedsModel: ",
        scheduleNeedsModel
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleNeedsModel]);

  // - - - - - - - - - -

  useEffect(() => {
    // 2025-07-25 - SW-681 present Modal
    if (dropObject.id !== "" && !processingDragNDrop) {
      // Silent drop: job dropped back on same cell it came from — skip modal entirely
      if (
        dragObject &&
        dropObject.id !== "Unassigned_RTS" &&
        dropObject.id.slice(0, 10) !== "Unassigned"
      ) {
        const draggedJob = scheduleArray.find((j) => j.id === dragObject);
        if (draggedJob && draggedJob.installer && draggedJob.start) {
          // Drop ID format (#105 phase 5): "<payrollNo>_<columnIndex>_<date?>"
          // The trailing date is required for multi-grid Optimizer; falls
          // back to AppContext.weekDatesArray for legacy Scheduler tab.
          const dropParts = dropObject.id.split("_");
          const droppedPayrollNo = dropObject.id.slice(0, 7);
          const droppedColumnIndex = parseInt(dropObject.id.slice(8));
          const encodedDate =
            dropParts.length >= 3 ? dropParts.slice(2).join("_") : null;
          const dropDate = encodedDate || weekDatesArray[droppedColumnIndex];
          const sameInstaller = droppedPayrollNo === draggedJob.installer;
          const sameDate = dropDate === draggedJob.start;
          if (sameInstaller && sameDate) {
            // If the job is parked, also unpark it
            if (isJobParked(dragObject)) {
              console.log(
                "%cScheduleContext: Silent unpark - returned to original cell",
                "color: #22c55e; font-weight: bold;",
                draggedJob.invoiceNo
              );
              unparkJob(dragObject);
            } else {
              console.log(
                "%cScheduleContext: Silent drop - returned to same cell",
                "color: #22c55e; font-weight: bold;",
                draggedJob.invoiceNo
              );
            }
            setDragObject("");
            setDropObject({ id: "" });
            return;
          }
        }
      }
      // Silent unpark: parked job returned to original crew+day — skip modal and mutation
      if (
        dragObject &&
        isJobParked(dragObject) &&
        dropObject.id !== "Unassigned_RTS"
      ) {
        const parkedJob = parkedJobs.find((pj) => pj.id === dragObject);
        if (parkedJob) {
          let droppedPayrollNo, dropDate;
          // Drop ID formats (#105 phase 5):
          //   Optimizer crew:    "<payrollNo>_<columnIndex>_<date?>"
          //   Optimizer unass:   "Unassigned_<DayName>_<date?>"
          //   Legacy Scheduler:  same minus trailing _<date>
          // Trailing date is required for multi-grid Optimizer; absent
          // (legacy Scheduler) means use AppContext.weekDatesArray.
          const dropParts = dropObject.id.split("_");
          const encodedDate =
            dropParts.length >= 3 ? dropParts.slice(2).join("_") : null;
          if (dropObject.id.slice(0, 10) === "Unassigned") {
            // Unassigned-row drop: parts = ["Unassigned", DayName, date?]
            const dayMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
            const dayName = dropParts[1];
            const dayIndex = dayMap[dayName];
            dropDate =
              encodedDate ||
              (dayIndex !== undefined ? weekDatesArray[dayIndex] : "");
            const draggedJob = scheduleArray.find((j) => j.id === dragObject);
            droppedPayrollNo = draggedJob?.installer || "";
          } else {
            // Crew-cell drop: parts = [payrollNo, columnIndex, date?]
            droppedPayrollNo = dropObject.id.slice(0, 7);
            const droppedColumnIndex = parseInt(dropObject.id.slice(8));
            dropDate = encodedDate || weekDatesArray[droppedColumnIndex];
          }
          const installerMatch = droppedPayrollNo === parkedJob.originalInstaller;
          const dateMatch = dropDate && dropDate === parkedJob.originalDateScheduled;
          if (installerMatch && dateMatch) {
            console.log(
              "%cScheduleContext: Silent unpark - returned to original slot",
              "color: #22c55e; font-weight: bold;",
              parkedJob.invoiceNo
            );
            unparkJob(dragObject);
            setDragObject("");
            setDropObject({ id: "" });
            return;
          }
        }
      }
      // - - - - #154 (2026-05-22) — Repair-Order-with-installer intercept.
      // Repair Orders arrive with `installer` pre-filled by FileMaker's
      // auto-invoice-create flow (the original installer). Before #154,
      // dragging silently overwrote that payroll#. We now open a
      // confirmation modal that asks the user which installer should keep
      // the assignment (or to clear it). Same-installer cell drops AND
      // same-cell silent drops fall through unchanged via the categorizer
      // returning `shouldPrompt: false`.
      const repairOrderFoundDoc = projectListAfterMergeRef.current?.find(
        (doc) =>
          String(doc.id || "") === dragObject ||
          String(doc.mongoId || "") === dragObject
      );
      if (isRepairOrderWithInstaller(repairOrderFoundDoc)) {
        const categorization = categorizeRepairOrderDrop({
          dropObject,
          foundDocument: repairOrderFoundDoc,
        });
        if (categorization.shouldPrompt) {
          console.log(
            "%cScheduleContext: #154 Repair Order with installer — opening confirm modal",
            "color: #f97316; font-weight: bold;",
            { invoiceNo: repairOrderFoundDoc.invoiceNo, dropType: categorization.dropType }
          );
          setOpenRepairOrderConfirmModal({
            show: true,
            foundDocument: repairOrderFoundDoc,
            dropType: categorization.dropType,
            dropTargetInstaller: categorization.dropTargetInstaller,
          });
          return;
        }
      }
      // 2026-01-28 - Unscheduling should open SuppressModal to capture reason
      // 2026-02-26 - Status 1/2 jobs revert directly (no suppress modal needed)
      if (dropObject.id === "Unassigned_RTS") {
        // Silent drop: RTS job dropped back on RTS — already there, skip modal
        const rtsCheck = scheduleArray.find((j) => j.id === dragObject);
        if (rtsCheck && parseFloat(rtsCheck.statusCode) === 3 && rtsCheck.start === "") {
          console.log(
            "%cScheduleContext: Silent drop - RTS job returned to RTS",
            "color: #22c55e; font-weight: bold;",
            rtsCheck.invoiceNo
          );
          setDragObject("");
          setDropObject({ id: "" });
          return;
        }

        // Derive the correct rollback status from the job's underlying FM fields:
        //   notVerifiedByStaff = true  → status 1 (Created, not verified)
        //   notMaterialReceived = true → status 2 (Verified, materials not yet received)
        //   otherwise                  → status 3 (RTS — use SuppressModal as normal)
        const rollbackStatus = rtsCheck
          ? (rtsCheck.notVerifiedByStaff ? 1 : rtsCheck.notMaterialReceived ? 2 : 3)
          : 3; // default to RTS if job not found in scheduleArray

        // Find the job being unscheduled (needed for mongoId)
        const foundJob = projectListAfterMergeRef.current?.find(
          (doc) =>
            String(doc.id || "") === dragObject ||
            String(doc.mongoId || "") === dragObject
        );

        if (foundJob) {
          if (rollbackStatus === 1 || rollbackStatus === 2) {
            // Status 1/2 jobs: revert directly — no reason/notes needed
            const mongoUsername = tsUserDocument?.username || "unknown";
            console.log(
              `%cScheduleContext: Reverting status-${rollbackStatus} job directly (no SuppressModal)`,
              "color: #f97316; font-weight: bold;",
              foundJob.invoiceNo
            );
            tsSetScheduleById.mutate({
              id: foundJob.mongoId || foundJob.id,
              updateFields: {
                statusCode: rollbackStatus,
                date_scheduled: "",
                date_estimatedCompletion: "",
                installer: "",
                extraCellId: null,
                extraCellText: null,
                history: new Date().toString().concat(" - ").concat(mongoUsername).concat(", tab:schedule"),
                _mongoUsername: mongoUsername,
              },
            });
            unparkJob(dragObject);
            setDragObject("");
            setDropObject({ id: "" });
          } else {
            // Status 3 jobs: SuppressModal to capture reason (unchanged behavior)
            setOpenSuppressModal({
              currentJob: {
                mongoId: foundJob.mongoId || foundJob.id,
                invoiceNo: foundJob.invoiceNo,
                classification: foundJob.classification,
              },
              show: true,
              suppress: {
                invoiceNo: foundJob.invoiceNo,
                classification: foundJob.classification,
              },
            });
            // Auto-unpark when a parked job is suppressed via RTS drop
            unparkJob(dragObject);
            // Signal UnassignedProjects to auto-expand the Suppressed accordion
            setAutoExpandSuppressed(true);
            // Clear drag state - SuppressModal handles the mutation
            setDragObject("");
            setDropObject({ id: "" });
          }
        } else {
          // Fallback to JobInitModal if job not found
          setOpenJobInitModal({ show: true });
        }
      } else {
        // Normal scheduling - use JobInitModal
        setOpenJobInitModal({ show: true });
      }
    }
    console.log("ScheduleContext useEffect[dropObject]...(openModal)");
    console.log(
      "ScheduleContext useEffect[dropObject] drag: ",
      dragObject,
      ", drop: ",
      dropObject
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropObject]);

  // - - - - - - - - - -

  useEffect(() => {
    // Modal Save() triggered...
    if (processingDragNDrop) {
      processDragNDrop();
      console.log(
        "ScheduleContext useEffect[processingDragNDrop]...(processDragNDrop)"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingDragNDrop]);

  // - - - - - - - - - - - - - - - - -  CONTEXT RETURN  - - - - - - - - - - - - - - - - - -

  // Time Machine #114 #120 — Parking is a client-side concept (React state +
  // localStorage) that is NOT tracked in EventLog or AuditSnapshots, so it's
  // not part of any historical snapshot. Force the parking surface to empty
  // during time-travel via the pure helper below so the Parked panel
  // naturally shows 0 jobs (matching the historical reality), JobTile's
  // isParkedOriginal flag is always false (so historical job tiles render
  // normally without the parked-tile dim treatment), and any drag-from-
  // parked path is inert (drag is also suppressed at the JobTile level via
  // #119, this is belt-and-suspenders).
  //
  // Live `parkedJobs` state and localStorage are UNTOUCHED — the user's
  // parked-jobs view is restored verbatim on time-travel exit (via the
  // existing useState + localStorage useEffects above; nothing here mutates
  // them).
  //
  // Companion: the auto-cleanup useEffect (~line 414) gates on !isTimeTraveling
  // to prevent it from evicting a live-parked job whose ID isn't in the
  // historical projectListAfterMerge — that would be silent data loss when
  // the user exits time-travel.
  const {
    parkedJobs: effectiveParkedJobs,
    parkJob: effectiveParkJob,
    unparkJob: effectiveUnparkJob,
    isJobParked: effectiveIsJobParked,
  } = gateParkingForTimeTravel({
    isTimeTraveling,
    parkedJobs,
    parkJob,
    unparkJob,
    isJobParked,
  });

  return (
    <ScheduleContext.Provider
      value={{
        schedulePreparing,
        scheduleArray,
        countsArray,
        // Per-week builders for multi-grid Optimizer (#105 phase 4a).
        // Each grid will call these with its own weekDatesArray; phase 4b
        // wires them up. They're pure-ish — no setState side effects, just
        // return the freshly-built array. All other deps come from this
        // ScheduleContext closure (location/projects/candidates/etc.).
        buildScheduleArrayForWeek,
        buildCountsArrayForWeek,
        furnitureArray,
        floatingArray,
        glueDownArray,
        nailDownArray,
        vinylArray,
        tileArray,
        backsplashArray,
        interiorDoorArray,
        exteriorDoorArray,
        stormDoorArray,
        windowArray,
        scheduleNeedsModel,
        dragObject,
        dropObject,
        processingDragNDrop,
        projectListQuery,
        userListQuery,
        selectedUnscheduledJob,
        setSchedulePreparing,
        setScheduleArray,
        setCountsArray,
        setScheduleNeedsModel,
        setFurnitureArray,
        setFloatingArray,
        setGlueDownArray,
        setNailDownArray,
        setVinylArray,
        setTileArray,
        setBacksplashArray,
        setInteriorDoorArray,
        setExteriorDoorArray,
        setStormDoorArray,
        setWindowArray,
        setDragObject,
        setDragObjectInvoiceNo,
        setDropObject,
        setProcessingDragNDrop,
        setPendingDragUpdate,
        setProjectListQuery,
        setUserListQuery,
        setSelectedUnscheduledJob,
        createScheduleArray,
        createCountsArray,
        createFurnitureArray,
        // Job Parking — gated to inert during time-travel (#120). Internal
        // raw versions remain in scope (parkedJobs/parkJob/unparkJob/
        // isJobParked) so any future internal-only use can pick them up
        // explicitly; consumers via context get the time-travel-safe ones.
        parkedJobs: effectiveParkedJobs,
        parkJob: effectiveParkJob,
        unparkJob: effectiveUnparkJob,
        isJobParked: effectiveIsJobParked,
        // Optimizer "Show 'OFF'" checkbox (#100)
        showOffInstallers,
        setShowOffInstallers,
        // Auto-expand Suppressed accordion
        autoExpandSuppressed,
        setAutoExpandSuppressed,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};
